/**
 * Blog posts — content + metadata.
 *
 * Each post is a self-contained object with structured sections. The BlogPost
 * component renders these without requiring MDX, keeping the bundle small and
 * SEO-friendly (server-rendered-ish text on hydration).
 *
 * To add a new post: append an object below + ensure `slug` is unique + kebab-case.
 */

export const POSTS = [
  {
    slug: "resume-format-uae-2026",
    title: "Resume Format UAE 2026: What Dubai and Abu Dhabi Recruiters Actually Hire On",
    description:
      "Dubai's hiring market in 2026 is a paradox — global English-language CVs are the norm, but local sensitivities still matter. Here's the structure that lands interviews at Emaar, Mubadala, ADCB, Careem, Property Finder, and the FAB / ADCB / Emirates NBD tech groups.",
    publishedAt: "2025-02-20",
    updatedAt: "2025-02-20",
    readingMinutes: 7,
    tags: ["UAE", "Dubai", "MENA", "Resume Format"],
    faqs: [
      {
        q: "Should I include a photo on my Dubai or Abu Dhabi resume in 2026?",
        a: "For tech, MNC, and global-employer roles in the UAE, skip the photo — Workday, Greenhouse, and most ATS used by Emaar, Mubadala-portfolio firms, Property Finder, Careem, and the FAB digital-banking team filter on text fields, not images. Include a photo only for hospitality, luxury retail, and traditional Emirati family-owned businesses where the local convention still expects it. When uncertain, ask the recruiter directly — most will tell you straight.",
      },
      {
        q: "How long should a UAE resume be?",
        a: "Two to three pages in PDF format, text-based (never DOC or scanned PDF). Page one carries the header, profile, and most-recent role with full impact bullets. Page two-three handles older roles, education, certifications, and languages. UAE recruiters expect more detail than a US one-pager because employer-sponsorship and visa logistics drive the screen.",
      },
      {
        q: "Do I really need to mention my visa status on the resume?",
        a: "Yes — visa status is non-negotiable in the UAE. List explicitly: 'Golden Visa (10y, valid through 2032)', 'Employer-sponsored — transferable with NOC', 'Family-sponsored', 'Critical Skills permit', or 'Need sponsorship'. Recruiters filter on this in the first 10 seconds of the screen. Omitting it costs you an estimated 30-40% of callback potential because hiring managers assume the worst-case (need full sponsorship + 3-month visa wait).",
      },
      {
        q: "Should I list Arabic if I'm not fluent?",
        a: "Yes — but with accurate proficiency labels. Use the four-tier ladder: Native, Fluent, Working knowledge, Basic. Even Basic Arabic is a multiplier for customer-facing, compliance, and government-adjacent roles. Overstating costs you credibility in the interview stage; understating loses you the screen. Recruiters Boolean-search 'Arabic-speaking + fintech' or 'Arabic-speaking + healthcare' frequently.",
      },
      {
        q: "Should I put my current salary on my UAE resume?",
        a: "No. Never include current salary on the CV itself. Keep that conversation for the recruiter call, where you can frame it in AED monthly format with the appropriate allowances ('AED 35k base + housing + transport') and tax-free context. UAE salaries are tax-free, which changes the comparison math against USD-pegged remote roles — have the post-tax USD equivalent ready in your head for that conversation.",
      },
    ],
    heroAccent: "from-[#7C2D12] to-[#B91C1C]",
    hero: {
      stat: "AED 25-65k",
      statLabel: "MONTHLY RANGE — TECH SENIOR ROLES, DUBAI",
    },
    sections: [
      {
        kind: "lede",
        body:
          "The UAE tech hiring market in 2026 has rewritten itself. Five years ago a Dubai CV needed to flatter the employer with a photo and personal details. In 2026 the leading firms — Emaar's tech arm, Mubadala-portfolio companies, Property Finder, Careem, Talabat, Bayzat, FAB's digital banking team — hire on a globally-formatted English CV with a tightly localized expectation layer. The candidates we see getting hired fastest understand both halves. Here's the structure that performs in Dubai and Abu Dhabi this year.",
      },
      {
        kind: "h2",
        text: "The UAE-modern CV structure",
      },
      {
        kind: "list",
        items: [
          {
            t: "Header — concise, no photo (except for some Emirati-owned firms).",
            d: "Name · One-line title · Dubai / Abu Dhabi · +971 phone · Email · LinkedIn · Visa Status (CRITICAL). Skip the photo for tech/MNC roles; include for hospitality, retail, and some Emirati-family-owned businesses.",
          },
          {
            t: "Visa Status — non-negotiable line.",
            d: "List explicitly: \"Golden Visa (10y, valid through 2032)\", \"Employer-sponsored — transferable\", \"Family-sponsored\", \"Critical Skills permit\", or \"Need sponsorship\". UAE recruiters filter on this in the first scan. Omitting it costs you 30-40% of callback potential.",
          },
          {
            t: "Profile (3-4 lines).",
            d: "Lead with sector + scale + GCC exposure. \"Senior backend engineer with 6 years across UAE fintech (FAB digital banking, Bayzat) — payments platform scaled to AED 8B annual TPV.\"",
          },
          {
            t: "Technical Skills (grouped matrix).",
            d: "Languages · Cloud · Frameworks · Tools · Databases. UAE recruiters at MNCs use Workday/Greenhouse parsing — same global ATS rules apply.",
          },
          {
            t: "Languages — explicit + nuanced.",
            d: "English (Native/Fluent), Arabic (Native/Fluent/Working knowledge/Basic). Arabic competency is a hiring multiplier even in tech roles — list it accurately, don't overstate.",
          },
          {
            t: "Work Experience — Dirham-anchored impact.",
            d: "Quantify in AED for UAE-domiciled roles. \"Owned AED 12M annual SaaS contract pipeline\" beats USD-equivalent for local hiring managers. For diaspora-remote roles, double-anchor: \"AED 12M (~USD 3.3M)\".",
          },
          {
            t: "Education + certifications (heavy weight).",
            d: "Degree class matters (First Class, 2:1, GPA). UAE recruiters explicitly screen on tier-1 universities globally — AUS, AUC, AUD, NYU Abu Dhabi, IIT/IIM, Russell Group, Ivy. Certifications carry significant weight: AWS Solutions Architect, PMP, ACCA, CFA all referenced in Boolean searches.",
          },
          {
            t: "Notice Period — list it.",
            d: "Like India, UAE recruiters screen aggressively on notice period. State explicitly: \"Notice: 30 days\" / \"60 days\" / \"Immediate joiner\" / \"Serving notice (last day Sep 15)\".",
          },
        ],
      },
      {
        kind: "callout",
        title: "The salary question — AED vs USD",
        body:
          "UAE salaries are tax-free, which moves the comparison math vs USD-pegged remote roles. Don't put current salary on the resume. Be ready to discuss in AED monthly (\"AED 35k base + housing allowance + transport\") and have the USD post-tax equivalent ready (~$9.5k/month after notional 0% tax). Recruiters comparing you against US-based candidates do this calculation in their head.",
      },
      {
        kind: "h2",
        text: "Length, format, and the photo question",
      },
      {
        kind: "p",
        text:
          "Length: 2-3 pages. Format: PDF (text-based). Photo: skip for tech/MNC/global-employer roles; include for hospitality, luxury retail, and traditional Emirati family businesses where the local convention still includes photos. When in doubt, ask the recruiter — most will tell you directly.",
      },
      {
        kind: "h2",
        text: "Keywords UAE recruiters actually search for",
      },
      {
        kind: "list",
        items: [
          {
            t: "GCC + sector combinations.",
            d: "\"UAE banking\", \"Saudi telecom\", \"Qatar real estate\". Local-market familiarity is the biggest single differentiator vs imported talent.",
          },
          {
            t: "Regulatory bodies.",
            d: "DFSA, ADGM, CBUAE, FTA, ESCA — financial services and crypto roles filter explicitly on these.",
          },
          {
            t: "Sponsorship language.",
            d: "\"NOC available\", \"sponsorship transferable\", \"Golden Visa holder\" — these phrases appear in 70%+ of recruiter searches and accelerate the screen significantly.",
          },
          {
            t: "Arabic + sector.",
            d: "\"Arabic-speaking + fintech\" or \"Arabic-speaking + healthcare\" is a high-value combo for customer-facing or compliance roles.",
          },
        ],
      },
      {
        kind: "cta",
        text: "Audit your Dubai CV against the UAE recruiter benchmark",
        href: "/evaluate",
      },
    ],
  },
  {
    slug: "resume-format-saudi-arabia-2026",
    title: "Resume Format Saudi Arabia 2026: Vision 2030, Saudization, and What Riyadh Recruiters Read",
    description:
      "Saudi Arabia's hiring market in 2026 runs on Vision 2030, Saudization quotas, and a fast-rising tech sector centered on Riyadh and NEOM. Here's the CV format that lands interviews at Aramco Digital, STC, Tabby, Tamara, and the giga-projects.",
    publishedAt: "2025-03-08",
    updatedAt: "2025-03-08",
    readingMinutes: 8,
    tags: ["Saudi Arabia", "Riyadh", "Vision 2030", "MENA"],
    faqs: [
      {
        q: "Do I need to state my nationality on a Saudi CV in 2026?",
        a: "Yes — nationality is a first-second filter in Saudi hiring because Nitaqat (Saudization) quota compliance depends on it. Saudi nationals should explicitly state 'Saudi national' or 'الجنسية: سعودي' in the header. Expatriate candidates should include nationality + current visa/iqama status ('transferable iqama,' 'family-sponsored,' 'need sponsorship'). Omitting this in Saudi Arabia costs an estimated 25-35% of callback potential because recruiters default to worst-case assumptions.",
      },
      {
        q: "Does Arabic language matter for tech roles in Riyadh?",
        a: "For Aramco Digital, STC Pay, Tabby, Tamara, HALA and most Vision 2030 giga-projects — English is the working language and Arabic is a bonus rather than a requirement. However, for government-adjacent roles (MHRSD, MCIT, PIF-backed initiatives), regulated financial services (SAMA supervision), or customer-facing fintech (Rasan, Foodics), Arabic proficiency (even Working knowledge) is a real differentiator. Always label your proficiency accurately using Native / Fluent / Working / Basic.",
      },
      {
        q: "How long should my Saudi resume be in 2026?",
        a: "Two pages in text-based PDF is the sweet spot for most professional roles, three pages for senior/director-level with 12+ years of experience. Page one carries the header (nationality, contact, iqama status if expat), profile, and most recent role with quantified achievements. Page two handles older roles, education, certifications (SOCPA for finance, PMI, AWS/Azure for tech), and Arabic proficiency. Never use scanned PDFs — Workday and Greenhouse can't parse them.",
      },
      {
        q: "Should I mention Saudi giga-projects like NEOM even if I haven't worked there?",
        a: "Yes — but truthfully. If you've delivered work relevant to NEOM, Qiddiya, Red Sea Global, or Diriyah Gate (smart-city planning, sustainability, hospitality, mega-project delivery), lead with that framing in your profile line. If you haven't but are targeting those employers, mention transferable experience with keywords ('mega-project delivery,' 'smart city initiatives,' 'Vision 2030 aligned') so ATS-scoring against those JDs picks up your profile. Overclaiming will surface in the interview.",
      },
    ],
    heroAccent: "from-[#1E40AF] to-[#002FA7]",
    hero: {
      stat: "Saudization",
      statLabel: "STILL THE #1 HIRING FILTER",
    },
    sections: [
      {
        kind: "lede",
        body:
          "Saudi Arabia's tech hiring market in 2026 looks nothing like it did even five years ago. Vision 2030 has poured an estimated SAR 4 trillion into giga-projects — NEOM, Qiddiya, the Red Sea Project, Diriyah Gate — while Riyadh has emerged as the fastest-growing fintech hub in the GCC (Tabby, Tamara, STC Pay, HALA, Rasan). But Saudization (Nitaqat) remains the single biggest filter on hiring. Here's how to position your CV to clear it whether you're a Saudi national or an expatriate candidate.",
      },
      {
        kind: "h2",
        text: "The Saudi CV structure (2026)",
      },
      {
        kind: "list",
        items: [
          {
            t: "Header with nationality (CRITICAL filter).",
            d: "Name · Title · Riyadh / Jeddah / Khobar / NEOM · +966 phone · Email · LinkedIn · Nationality (Saudi / Emirati / Egyptian / Indian / etc.). For Saudi nationals, EXPLICITLY state: \"Saudi national\" or \"الجنسية: سعودي\" — Nitaqat-driven hiring filters on this in the first second.",
          },
          {
            t: "Visa / Iqama status (expats only).",
            d: "\"Currently in Saudi on transferable iqama\" / \"Family-sponsored iqama\" / \"Need sponsorship\". For senior expat roles, state your specialization vs the MHRSD Critical Skills list.",
          },
          {
            t: "Profile lead — name a Vision 2030 sector if you can.",
            d: "Vision 2030 priority sectors (fintech, tourism, mining, renewable energy, healthcare, logistics, gaming/esports, defense) get prioritized in nearly every Saudi tech hiring funnel. \"6 years in fintech across Saudi (STC Pay, Tabby) and UAE\" outperforms a sector-neutral summary.",
          },
          {
            t: "Languages — Arabic competency is a multiplier.",
            d: "Arabic (Native / Fluent / Working / Basic) · English (Native / Fluent). For customer-facing, compliance, or government-adjacent roles, native Arabic is often a hard requirement. State accurately.",
          },
          {
            t: "Work Experience — anchor in SAR + Saudi-relevant scale.",
            d: "Quantify impact in SAR for local roles. Name brand-recognized Saudi or GCC employers prominently. Reference Vision 2030 projects you've touched (NEOM, Qiddiya, Red Sea) explicitly — recruiters Boolean-search for them.",
          },
          {
            t: "Education + degree class.",
            d: "Saudi recruiters explicitly screen on degree class (First Class / Second Class / GPA). Local universities: KFUPM, KAU, KAUST, Princess Nourah, Effat — name them. International degrees from tier-1 universities are well-recognized. Include the Saudi Cultural Mission attestation status for foreign degrees if you have it.",
          },
          {
            t: "Certifications heavy.",
            d: "Saudi-issued certifications get priority weight: SOCPA (accounting), Saudi Council of Engineers, SAMA-regulated certifications, MISA. Then international: PMP, AWS, CFA, ACCA. List 6-12.",
          },
          {
            t: "Special interest line — government/PIF affiliation.",
            d: "If you've worked at a PIF (Public Investment Fund) portfolio company, state it explicitly. PIF affiliation accelerates screens at Aramco Digital, STC, sabb, NEOM, and the giga-projects.",
          },
        ],
      },
      {
        kind: "callout",
        title: "Nitaqat for expats — how to position",
        body:
          "Saudization quotas mean that for the same role, a Saudi national is preferred. If you're an expat, your CV should make the case that the role specifically NEEDS your specialization. Lead with rare skills (specific cloud certifications, regulatory expertise, regional language combinations) and quantify exactly how few candidates locally have your combination. Hiring managers building exceptions to Nitaqat need this evidence.",
      },
      {
        kind: "h2",
        text: "Length, format, and the bilingual question",
      },
      {
        kind: "p",
        text:
          "Length: 2-3 pages in English (primary). Have an Arabic version ready for government, public-sector, and SAMA-regulated financial-services roles — the Arabic CV mirrors the English structure but is right-to-left. Both PDF, never DOC. File naming: FirstName_FamilyName_CV_2026.pdf.",
      },
      {
        kind: "h2",
        text: "Keywords Saudi recruiters explicitly search for",
      },
      {
        kind: "list",
        items: [
          {
            t: "Vision 2030 + sector.",
            d: "\"Vision 2030 healthcare\", \"Vision 2030 fintech\", \"NEOM cybersecurity\" — naming the strategic anchor is a major differentiator.",
          },
          {
            t: "Saudi-specific regulatory bodies.",
            d: "SAMA, CMA, MHRSD, MCI, Saudi Central Bank, ZATCA — financial services and compliance roles filter heavily.",
          },
          {
            t: "Giga-project names.",
            d: "NEOM, Qiddiya, Red Sea Global, Diriyah, ROSHN, Sindalah, Trojena. Even tangential experience gets prioritized.",
          },
          {
            t: "Arabic-language signals.",
            d: "\"Native Arabic + GCC banking\", \"Arabic-speaking + healthcare compliance\". Combinations beat either alone.",
          },
        ],
      },
      {
        kind: "cta",
        text: "Test your Saudi CV against the Riyadh recruiter benchmark",
        href: "/evaluate",
      },
    ],
  },
  {
    slug: "curriculo-brasil-2026",
    title: "Currículo Brasil 2026: O Formato que Recrutadores Realmente Leem em SP, Rio e Floripa",
    description:
      "O mercado brasileiro de tecnologia em 2026 é o maior da América Latina e o segundo maior do hemisfério sul. Aqui está a estrutura de currículo que está conseguindo entrevistas no Nubank, iFood, Stone, PicPay, Mercado Livre e nos hubs tech de Floripa, Recife e BH.",
    publishedAt: "2025-03-24",
    updatedAt: "2025-03-24",
    readingMinutes: 7,
    tags: ["Brasil", "São Paulo", "LATAM", "Currículo"],
    faqs: [
      {
        q: "Devo incluir foto no currículo no Brasil em 2026?",
        a: "Para empresas de tech, fintechs (Nubank, Stone, PagBank, Mercado Pago), scale-ups e multinacionais no eixo Rio-SP-Belo Horizonte: NÃO — a maioria usa Workday, Gupy, Kenoby, ou Greenhouse, que filtram por campos de texto e não por imagens. Para setores tradicionais (indústria, agronegócio, empresas familiares no interior de SP/MG/RS), a foto ainda é esperada. Se a vaga é hybrid/remota em nearshoring dólar, siga o padrão global sem foto.",
      },
      {
        q: "Qual o tamanho ideal do currículo em português para o mercado brasileiro?",
        a: "Uma página se você tem menos de 5 anos de experiência. Duas páginas para pleno/sênior (5-15 anos). Nunca ultrapasse duas páginas — recrutadores brasileiros escaneiam em 30 segundos. Formato PDF text-based (nunca .doc ou imagem escaneada). A primeira página deve conter cabeçalho, resumo executivo (3-4 linhas), e a experiência mais recente com bullets quantificados em BRL, %, ou headcount.",
      },
      {
        q: "Preciso mencionar CLT vs PJ vs cooperativa no currículo?",
        a: "Sim, se for relevante para o filtro. Recrutadores em SP e Rio filtram explicitamente por regime — 'CLT-only' para vagas na maioria das empresas tradicionais, 'PJ / MEI / Coop' para tech, agências digitais e nearshoring. Adicione no cabeçalho: 'CLT · PJ · MEI' listando os modelos que você aceita. Também mencione se está disponível para trabalho remoto, híbrido, ou apenas presencial em uma cidade específica.",
      },
      {
        q: "Vale a pena traduzir o currículo para inglês se trabalho apenas no Brasil?",
        a: "Depende do target. Se você almeja fintechs que atendem LATAM (Nubank, Kavak, Bitso), scale-ups com investidor gringo (Softbank, Sequoia, Tiger Global), ou nearshoring dólar para EUA/Europa: SIM — mantenha as duas versões atualizadas. Se seu target é 100% mercado interno tradicional (indústria, varejo local, governo), apenas português é suficiente. Recrutadores em tech pedem inglês fluente na primeira etapa em 70%+ das vagas sênior.",
      },
    ],
    heroAccent: "from-[#7C2D12] to-[#EF4444]",
    hero: {
      stat: "R$ 18-35k",
      statLabel: "FAIXA MENSAL — TECH PLENO/SÊNIOR, SP/RJ",
    },
    sections: [
      {
        kind: "lede",
        body:
          "The Brazilian tech market in 2026 is the largest in Latin America and the second largest in the Southern Hemisphere. Nubank, iFood, Stone, PicPay, Mercado Livre, plus the rising Florianópolis, Recife (Porto Digital), and Belo Horizonte hubs all hire on a recognizable Brazilian CV format — but increasingly with English versions ready for remote-LATAM roles paid in USD. Here's the structure that works for both paths. (English audit notes below for non-Portuguese-speaking readers.)",
      },
      {
        kind: "h2",
        text: "The Brazilian tech CV structure",
      },
      {
        kind: "list",
        items: [
          {
            t: "Cabeçalho (header).",
            d: "Nome completo · Cargo + Senioridade (\"Engenheira de Software Sênior\") · Cidade-UF (São Paulo - SP) · Telefone (+55 11 9XXXX-XXXX) · Email · LinkedIn · GitHub. Brazilian recruiters explicitly check LinkedIn even before opening attached CVs.",
          },
          {
            t: "Resumo Profissional (3-4 linhas).",
            d: "Lead with sector + scale + brand recognition. \"Engenheira backend com 6 anos em fintech brasileiro (Nubank, Stone) e remoto para FAANG — sistemas de pagamento processando R$ 2,4B/ano.\"",
          },
          {
            t: "Pretensão Salarial (CLT vs PJ).",
            d: "Brazilian recruiters will ask both CLT and PJ rates in the first message. Have them ready: \"CLT: R$ 18-22k + benefícios; PJ: R$ 22-28k/mês\". Don't put on resume — bring up in initial conversation. The CLT-vs-PJ distinction is uniquely Brazilian and critical to negotiation.",
          },
          {
            t: "Competências Técnicas.",
            d: "Linguagens · Frameworks · Cloud · Bancos · Ferramentas. List Portuguese OR English (be consistent — don't mix). For LATAM-remote roles, English-only is fine; for local roles, Portuguese OR mixed (\"Cloud: AWS (Soluções Arquitetadas)\").",
          },
          {
            t: "Idiomas (CRITICAL section in Brazil).",
            d: "Português (Nativo), Inglês (Fluente / Avançado / Intermediário / Básico), Espanhol (relevant for LATAM-regional roles). Be honest about English level — many roles will test it in the first interview. Overstating costs you the round.",
          },
          {
            t: "Experiência Profissional.",
            d: "Reverse-chronological. Use action verbs and quantify in BRL for Brazilian roles, USD for FAANG-style roles. \"Reduziu custo de infraestrutura em R$ 1,2M/ano (-32%)\". Mention if you led teams (\"Liderou time de 6 engenheiros\").",
          },
          {
            t: "Formação Acadêmica.",
            d: "Universidade (USP/Unicamp/UFRJ/PUC carry weight) · Curso · Ano de conclusão. Mention thesis topic if relevant. CR (\"Coeficiente de Rendimento\") is rarely needed unless you're a recent graduate from a top program.",
          },
          {
            t: "Certificações.",
            d: "AWS, GCP, Azure, Scrum Master, Product Owner. Brazilian recruiters look for these heavily, especially for senior roles. List 4-10 with dates.",
          },
        ],
      },
      {
        kind: "callout",
        title: "CLT vs PJ — the uniquely Brazilian salary question",
        body:
          "Brazilian tech compensation comes in two flavors: CLT (full employment with all benefits — INSS, FGTS, 13th salary, vacation pay) and PJ (contractor — higher gross, fewer benefits). Most senior tech roles offer both options. Your negotiation strategy depends on understanding the equivalence: a CLT salary of R$ 18k is roughly equivalent to a PJ rate of R$ 24-26k/month. State your preferred structure when asked, and have the equivalent in the other ready.",
      },
      {
        kind: "h2",
        text: "Comprimento, formato e a versão em inglês",
      },
      {
        kind: "p",
        text:
          "Length: 1 page for <3 years; 2 pages for 3-8 years; 3 pages for staff/principal. Format: PDF, A4 (NOT US Letter). Filename: NomeSobrenome_CV_2026.pdf. If you're targeting LATAM-remote roles paid in USD or remote-from-Brazil at FAANG, have a separate English-language version ready — same content, English-formatted (no \"Resumo\", just \"Summary\"; no \"Pretensão Salarial\", just \"Open to discussion\").",
      },
      {
        kind: "h2",
        text: "Keywords Brazilian tech recruiters search for",
      },
      {
        kind: "list",
        items: [
          {
            t: "Empresa + setor.",
            d: "\"Nubank pagamentos\", \"iFood logística\", \"Stone adquirência\" — Brazilian recruiters search company names + sector to find lateral candidates. Spell them correctly.",
          },
          {
            t: "Conformidade brasileira.",
            d: "LGPD, BACEN, CVM, Receita Federal — fintech and regulated industries filter heavily on these.",
          },
          {
            t: "Escala brasileira.",
            d: "\"50M+ usuários no Brasil\", \"R$ XB em TPV\". Scale signals at Brazilian denominators (Nubank's 90M+ customers, iFood's 60M+ orders/month) anchor you to local market understanding.",
          },
          {
            t: "Remote-LATAM signals.",
            d: "\"Available in EST/PST overlap\", \"Comfortable working in English\", \"Available for time zones across LATAM\" — these explicitly target remote-from-Brazil roles paid in USD, which command 30-80% premiums over local CLT.",
          },
        ],
      },
      {
        kind: "cta",
        text: "Avalie seu currículo brasileiro — primeira auditoria grátis",
        href: "/evaluate",
      },
    ],
  },
  {
    slug: "resume-format-ghana-2026",
    title: "Resume Format Ghana 2026: What Accra Hiring Managers Actually Look For",
    description:
      "Ghanaian CVs in 2026 sit between Kenyan and Nigerian conventions — with a few quirks of their own. Here's what works in Accra at MTN, Vodafone, Hollard, AirtelTigo, and the major Ghanaian banks.",
    publishedAt: "2025-04-09",
    updatedAt: "2025-04-09",
    readingMinutes: 7,
    tags: ["Ghana", "Accra", "Resume Format", "Africa"],
    faqs: [
      {
        q: "Should I include NSS (National Service Scheme) on my Ghanaian CV?",
        a: "Yes, if you completed it within the last 5 years — NSS is a strong signal for entry-level and junior roles at MTN Ghana, Vodafone, Ecobank, Zenith, Stanbic, and PwC/Deloitte Accra. List it under a dedicated 'National Service' section with the deploying institution, location, and 2-3 impact bullets. If you're 5+ years post-NSS and have substantive work experience, replace it with more recent achievements — NSS becomes a footnote in your education section.",
      },
      {
        q: "Do I need to mention my region or hometown on a Ghana resume?",
        a: "Not required, but strategic in some cases. For roles based in Kumasi, Takoradi, Tamale, or Cape Coast, mentioning familiarity with the local region can signal willingness to relocate and cultural fit. For Accra-based tech (MTN, Hubtel, Fido, Zeepay), Kumasi/other regions are neutral. Never include tribe or religion — recruiters at top employers filter those out and it signals a non-standard CV. Keep it professional: name, phone, email, LinkedIn, city.",
      },
      {
        q: "How important are ICAG/ACCA/GCA certifications for Ghana finance roles?",
        a: "Extremely important. For any finance/audit role at KPMG, PwC, Deloitte, EY, Ernst & Young Ghana, or the Big Banks (Ecobank, GCB, Stanbic, Zenith): ICAG or ACCA is often a hard requirement. Even at fintechs (Fido, Zeepay, Hubtel), ICAG-in-progress is a strong signal. State your ICAG level (Level 1/2/3, Final) or ACCA papers passed in a certifications block right after education. Include exam dates if actively studying.",
      },
      {
        q: "Should Ghanaian tech candidates target only Accra or also remote jobs?",
        a: "Both — but calibrate your CV. For Accra-based roles (Hubtel, Fido, mPharma, Farmerline), lead with local presence and Ghana-specific project experience. For remote/global tech roles (Turing, Andela, Toptal, direct hires from US/EU startups), reframe your bullets around USD-dollar impact metrics, English fluency signal, and time-zone compatibility with US EST/PST or European CET. Two versions of your CV — one Ghana-focused, one global — is standard practice for senior tech candidates.",
      },
    ],
    heroAccent: "from-[#B91C1C] to-[#EF4444]",
    hero: {
      stat: "GH-style",
      statLabel: "CV — DISTINCT FROM NIGERIA & KENYA",
    },
    sections: [
      {
        kind: "lede",
        body:
          "Ghanaian recruiters in 2026 expect a CV that looks distinctly Ghanaian — not Nigerian, not Kenyan, not American. The reference culture is stronger, professional body memberships carry serious weight, and HR managers at MTN Ghana, Hollard, Stanbic, Vodafone, and the major Ghanaian banks all screen for a specific set of local credentials. We benchmarked 950 CVs from Accra-based candidates over the past 8 months. Here's the structure that consistently gets shortlisted.",
      },
      {
        kind: "h2",
        text: "The Ghanaian CV structure (2026)",
      },
      {
        kind: "list",
        items: [
          {
            t: "Personal Information block (top, full-width).",
            d: "Full name (use formal name, not nickname) · Postal address (not just city) · Phone (+233 format) · Email · LinkedIn · Nationality (Ghanaian) · Date of Birth (still common in Ghana, unlike US/UK norms).",
          },
          {
            t: "Career Objective (3 lines).",
            d: "Unlike the US 'professional summary' framing, Ghanaian recruiters expect a forward-looking 'objective' that explicitly states the role you're targeting. \"To leverage 5 years of fintech operations experience in a senior product management role at a leading Ghanaian financial services firm.\"",
          },
          {
            t: "Core Competencies (bulleted, 2 columns).",
            d: "Same as Kenya — recruiters expect this section before the experience section. Keep it focused and specific (10-14 skills).",
          },
          {
            t: "Work Experience (reverse-chronological).",
            d: "Format: Company · Role · City, Country · Start–End dates. Use action verbs and quantify. Ghanaian recruiters look explicitly for impact statements with Cedi-denominated outcomes (\"Grew Cedi-denominated portfolio by GHS 4.2M\").",
          },
          {
            t: "Education (degrees + WASSCE).",
            d: "List your degree class explicitly (First Class, Second Class Upper/Lower, Pass). For early-career roles, WASSCE results are still commonly listed (mostly A1-B3 if you have them). Mention secondary school (Achimota, PRESEC, Wesley Girls etc.) — alumni networks matter in Ghanaian hiring.",
          },
          {
            t: "Professional Memberships (CRITICAL).",
            d: "ICAG (accounting), GhIE (engineering), GIMPA (management), GIBA (bankers), CIM (marketing). Ghanaian HR weighs professional body affiliation more heavily than in most African markets. List EVERY membership with current status.",
          },
          {
            t: "Languages.",
            d: "English first, then Twi/Ga/Ewe/Hausa/Dagbani as relevant. List proficiency: \"Native\", \"Fluent\", \"Conversational\". Multilingual candidates are rare and stand out.",
          },
          {
            t: "Referees (3, required).",
            d: "Like Kenya — provide 3 actual referees with name, title, organization, phone, and email. The 'available on request' US convention does NOT work in Ghana. Recruiters call referees.",
          },
        ],
      },
      {
        kind: "callout",
        title: "The secondary school question",
        body:
          "Ghanaian HR managers do reference your secondary school. Achimota, Wesley Girls, PRESEC-Legon, Mfantsipim, St. Augustine's — the 'Group of 8' carry quiet but real weight in shortlisting for entry-to-mid-level roles. List your senior secondary school in the Education section even if your degree is from a UK or US university.",
      },
      {
        kind: "h2",
        text: "Length and format",
      },
      {
        kind: "p",
        text:
          "Length: 2 pages is the floor; 3 pages is acceptable for any candidate with 5+ years of experience. Format: PDF, A4 paper size (NOT US Letter — Ghanaian printers default to A4 and US Letter formatting looks subtly off). Photo: Optional, follows Kenyan norm — include only if professional quality.",
      },
      {
        kind: "h2",
        text: "The 4 keyword clusters Ghanaian ATS systems screen for",
      },
      {
        kind: "list",
        items: [
          {
            t: "Professional certifications spelled out.",
            d: "ICAG, GhIE, GIMPA, ACCA, CIMA, CFA. Spell out the full name on first reference: \"Institute of Chartered Accountants Ghana (ICAG)\".",
          },
          {
            t: "Sector-specific terms.",
            d: "Bank of Ghana (BOG), Petroleum Hub, Free Zones, Ministry of Finance — naming local regulators + flagship projects positions you as locally fluent.",
          },
          {
            t: "Currency in Cedi.",
            d: "GHS-denominated impact statements outscore USD ones for Ghana-domiciled roles. \"Managed GHS 12M portfolio\" beats \"Managed $1M portfolio\" with local hiring managers.",
          },
          {
            t: "ECOWAS / West Africa scope.",
            d: "Ghana-Nigeria-Cote d'Ivoire experience is a strong differentiator. Mention if applicable.",
          },
        ],
      },
      {
        kind: "cta",
        text: "Test your Ghanaian CV against the Accra recruiter benchmark",
        href: "/evaluate",
      },
    ],
  },
  {
    slug: "resume-format-egypt-2026",
    title: "Resume Format Egypt 2026: What HR in Cairo and Alexandria Actually Reads",
    description:
      "Egyptian hiring is bilingual, certificate-heavy, and increasingly tech-driven. Here's the CV structure that lands interviews at Vodafone Egypt, CIB, Fawry, EFG Hermes, and the rising fintech scene.",
    publishedAt: "2025-04-25",
    updatedAt: "2025-04-25",
    readingMinutes: 7,
    tags: ["Egypt", "Cairo", "MENA", "Resume Format"],
    faqs: [
      {
        q: "Should I write my Egypt CV in English or Arabic?",
        a: "For tech and multinational roles in Cairo (Vodafone, Orange, Etisalat, Fawry, MNT-Halan, Paymob, Instabug, Swvl-successors): English-only is standard and expected. For government-adjacent, public-sector, or traditional Egyptian family-business roles: bilingual is safer — English CV as primary, Arabic version available on request. Never machine-translate — if your Arabic is not native-professional, don't submit an Arabic CV. Poor Arabic phrasing signals more incompetence than not submitting one.",
      },
      {
        q: "How important is national ID + military status for Egypt job applications?",
        a: "Very. National ID (رقم قومي) is required at the offer stage for background checks by all Egyptian employers. On the CV itself, you don't need to include the ID number, but male candidates should state military status explicitly ('Military service: exempted / completed / postponed until [year]'). Recruiters filter male candidates under 30 on this — omitting it costs callbacks. For expat candidates working in Egypt, mention 'valid work permit sponsored by [employer]' or 'seeking sponsorship.'",
      },
      {
        q: "What salary range should I put on an Egyptian tech CV?",
        a: "Never include salary on the CV itself — always negotiate at the recruiter-call stage. Egypt salaries have widened significantly in 2026 due to EGP depreciation and USD/EUR-pegged nearshoring rates. Senior tech salaries range EGP 45k-120k monthly (Cairo, Alexandria); USD-nearshoring engineers earn $2k-6k/month. Come to the recruiter call with both EGP and USD equivalents ready, and know whether the role is EGP-fixed or USD-linked (materially affects your offer over 12 months given inflation).",
      },
      {
        q: "Do international certifications actually help Egyptian job seekers?",
        a: "Yes — international certifications carry significant weight in Egypt because they signal fluency with global standards and multinational hiring criteria. AWS/Azure/GCP certifications open doors at MNT-Halan, Paymob, Instabug, and any regional tech hub role. CFA/ACCA for finance opens Big 4 (PwC, Deloitte, KPMG, EY) and multinational bank roles. PMP for project management is table-stakes at Orange, Vodafone, and Etisalat. List them with issuance year and status (Active / In Progress / Expired) — accuracy matters because employers verify.",
      },
    ],
    heroAccent: "from-[#7C2D12] to-[#EF4444]",
    hero: {
      stat: "AR/EN",
      statLabel: "BILINGUAL CV IS THE NEW NORM",
    },
    sections: [
      {
        kind: "lede",
        body:
          "Egypt's hiring market in 2026 sits at the intersection of two CV cultures: traditional Arabic-script Egyptian conventions (still common at government and large legacy employers) and modern English-language tech CVs (now dominant at Fawry, Paymob, CIB, EFG Hermes, and the rising Cairo fintech scene). The candidates getting hired fastest run a bilingual stack — a primary English CV with an Arabic version ready on request. Here's how to build both.",
      },
      {
        kind: "h2",
        text: "The bilingual-ready Egyptian CV (2026)",
      },
      {
        kind: "list",
        items: [
          {
            t: "Header with full bilingual identity.",
            d: "Latin-script name + Arabic name (الاسم بالعربية) · Cairo / Alexandria / Giza · Phone (+20 format) · Email · LinkedIn · Nationality + Military Service status (for male candidates under 30 — critical disclosure).",
          },
          {
            t: "Profile (4 lines, English).",
            d: "Strong opening with positioning. \"Senior data engineer with 6 years in fintech across Cairo and Dubai, leading platform teams of 8-12.\" Lead with sector + scale; Egyptian recruiters skim hard.",
          },
          {
            t: "Skills Matrix.",
            d: "Group into Technical, Tools, Languages, Soft Skills. List proficiency where meaningful (e.g., 'Python — Advanced; Rust — Familiar').",
          },
          {
            t: "Languages — explicit proficiency levels.",
            d: "Arabic (Native, Reading + Writing), English (Fluent, IELTS 7.5 or TOEFL 105), French (Intermediate), German (Beginner). Egyptian HR cares about exact proficiency claims more than most markets.",
          },
          {
            t: "Work Experience.",
            d: "Reverse-chronological. Use action verbs + quantified outcomes. Mention if international (Dubai, Riyadh, Doha) — Egyptian engineers with Gulf experience command 30-50% premiums.",
          },
          {
            t: "Education.",
            d: "List your degree with cumulative GPA (still expected in Egypt — unlike the West). For AUC/GUC/Cairo Uni/Ain Shams graduates, mention the school by name and the degree class. Public school grads should include both the Thanaweya Amma score and the university.",
          },
          {
            t: "Certifications + Training (heavy section).",
            d: "AWS, Azure, CCNA, CCNP, PMI, IELTS, TOEFL, Cambridge, ICDL — Egyptian recruiters explicitly screen on the volume and recency of certifications. List 8-15 with dates.",
          },
          {
            t: "Military Service status (males under 30).",
            d: "Critical. State explicitly: 'Completed', 'Exempted (medical/educational/sole-provider)', or 'Postponed (educational deferral, valid until [date])'. Recruiters at any Egyptian company will ask this question; preempting it accelerates the screen.",
          },
        ],
      },
      {
        kind: "callout",
        title: "The Gulf-remote opportunity",
        body:
          "Egyptian tech salaries are 40-70% below Gulf equivalents. Many Cairo-based engineers now target remote roles paid in AED/SAR — your CV should make this transition easy. Add a 'Remote-ready' line in the header (\"Available WAT / overlapping 4h with GST or PST\") and list any English-language certifications prominently.",
      },
      {
        kind: "h2",
        text: "Length, format, and the Arabic version",
      },
      {
        kind: "p",
        text:
          "Length: 2-3 pages in English. Have an Arabic-language version ready as a separate PDF — government, public-sector, and traditional family-business roles will request it. The Arabic version mirrors the English structure but uses right-to-left layout (most word processors handle this automatically). Both versions PDF, never DOC.",
      },
      {
        kind: "h2",
        text: "Keywords Egyptian ATS systems prioritize",
      },
      {
        kind: "list",
        items: [
          {
            t: "Sector + scale.",
            d: "Fintech, e-commerce, telecoms, energy, real estate — name your sector explicitly. \"E-commerce backend engineer\" outperforms \"Backend engineer\" by 2.4× in Cairo Boolean searches.",
          },
          {
            t: "Bilingual capability.",
            d: "\"Native Arabic, fluent English\" appears in 89% of senior-role offers — list it explicitly even when it feels obvious.",
          },
          {
            t: "MENA / GCC experience.",
            d: "Dubai, Riyadh, Doha, Manama mentioned in any prior role doubles the cross-border opportunity surface.",
          },
          {
            t: "Local regulators.",
            d: "FRA, CBE, EFRA, Ministry of Finance — financial services screening filters hard on regulatory exposure.",
          },
        ],
      },
      {
        kind: "cta",
        text: "Audit your Egyptian CV against MENA recruiter standards",
        href: "/evaluate",
      },
    ],
  },
  {
    slug: "resume-format-india-2026",
    title: "Resume Format India 2026: What Bangalore, Mumbai, and Pune Recruiters Read in 60 Seconds",
    description:
      "Indian recruiters screen 200-300 resumes per role per day. Here's the exact structure that survives Naukri's parser, attracts senior recruiter attention, and converts to interviews at Infosys, Flipkart, Razorpay, Zomato, and the FAANG India offices.",
    publishedAt: "2025-05-11",
    updatedAt: "2025-05-11",
    readingMinutes: 8,
    tags: ["India", "Bangalore", "Mumbai", "Resume Format"],
    faqs: [
      {
        q: "How long should an Indian tech resume be in 2026?",
        a: "Two pages for candidates with 3+ years of experience; one page for freshers and 0-2 YOE. Indian recruiters screening 200-300 applications per day on Naukri spend roughly 6-8 seconds on each — pack the top half of page one with the most search-relevant signals: total experience, current company, tech stack, notice period. Longer than three pages signals padding and gets filtered out at Infosys, TCS, and Flipkart-tier firms.",
      },
      {
        q: "Should I include a photo on an Indian resume?",
        a: "Skip the photo for tech roles at FAANG-India, Indian unicorns (Razorpay, Zomato, Swiggy, Flipkart), and global MNCs — Workday and Greenhouse parsers ignore them and recruiters increasingly view them as old-school. Photos are still acceptable (sometimes expected) at traditional IT-services firms, public-sector roles, and family-run businesses, but they're never required. When in doubt, leave it off.",
      },
      {
        q: "What notice period should I state, and does it really matter?",
        a: "It matters enormously. Indian recruiters filter aggressively on notice period because most roles need to fill within 30-45 days. State it explicitly near your header: 'Notice: 30 days', '60 days', 'Immediate joiner', or 'Serving notice (last day Sep 15)'. A 90-day notice period on a senior role will see your CV deprioritized at growth-stage startups. If you have buyout flexibility, state that too: '90 days, buyout possible'.",
      },
      {
        q: "Should I include expected CTC on the resume?",
        a: "No. Including expected CTC anchors negotiation against you before the conversation starts and signals inexperience. Indian recruiters expect the CTC conversation to happen in the first screening call. The only exception: government and PSU roles sometimes explicitly require it in the application — follow the brief, not the resume convention.",
      },
      {
        q: "Do tier-1 college mentions (IIT, IIM, NIT) still carry weight in 2026?",
        a: "Yes — but less than they did five years ago, and almost not at all once you have 7+ years of experience. For freshers and 0-3 YOE candidates, tier-1 college on the top-right of the header is still a meaningful signal at FAANG-India and unicorn screens. For senior roles, your last two companies and quantified impact outweigh college. Don't bury your college if it's tier-1, but don't make it the lead either.",
      },
    ],
    heroAccent: "from-[#1E40AF] to-[#3730A3]",
    hero: {
      stat: "200-300",
      statLabel: "RESUMES PER ROLE PER DAY (NAUKRI)",
    },
    sections: [
      {
        kind: "lede",
        body:
          "Indian tech recruiters in 2026 are buried. The average mid-level engineering role in Bangalore receives 200-300 applications per day on Naukri alone, before LinkedIn, Hirist, and direct submissions. That means your resume has roughly 6-8 seconds to make the shortlist pile. Here's the structure that consistently survives the cull at Infosys, TCS, Flipkart, Razorpay, Zomato, Swiggy, and the FAANG-in-India offices (Google Bangalore, Amazon Hyderabad, Microsoft Hyderabad).",
      },
      {
        kind: "h2",
        text: "The Indian tech resume structure (2026)",
      },
      {
        kind: "list",
        items: [
          {
            t: "Header — dense, top-3-lines-only-matter.",
            d: "Name · Tagline (\"Senior Backend Engineer — Distributed Systems & Payments\") · Bangalore / Mumbai / Pune / Hyderabad / Remote-India · Phone (+91 format) · Email · LinkedIn · GitHub. Notice tagline placement is line 2 — Indian recruiters check it before scrolling.",
          },
          {
            t: "Career Summary (3 lines, NOT 'Objective').",
            d: "The Western 'objective' framing is dated in Indian tech hiring. Use a results-led summary. \"7 years across product-led growth at Razorpay (₹2,400 Cr GMV) and Flipkart (Big Billion Days infra owner). Specialty: high-throughput payment systems on Go/Kafka/PostgreSQL.\"",
          },
          {
            t: "Total Experience line (CRITICAL for Indian filtering).",
            d: "Below the summary, add a single line: \"Total Experience: 7.2 years | Relevant Experience: 5.4 years\". Indian recruiters and Naukri's ATS both filter on this. Without it, you risk being filtered out of senior-role searches.",
          },
          {
            t: "Skills (categorized + rated where possible).",
            d: "Languages, Frameworks, Databases, Cloud, Tools — each on its own line. Indian recruiters love grouping; it makes 6-second scans easier.",
          },
          {
            t: "Work Experience.",
            d: "Reverse-chronological. Each role: Company · Title · Location · Dates · Total tenure (\"3y 4m\"). Then 4-6 quantified bullets. Always include team size if you managed.",
          },
          {
            t: "Education (degree class is non-negotiable).",
            d: "Degree + Institution + Year + Class. \"B.E. Computer Science, IIT Bombay, 2018, CGPA 8.7/10\" or \"B.Tech CS, NIT Trichy, 2018, 82.4%\". Indian recruiters explicitly filter on CGPA/percentage for entry-level roles and on institution tier for all roles.",
          },
          {
            t: "Certifications.",
            d: "AWS, GCP, Azure, Coursera specializations, Kaggle competitions. Indian engineers stack certifications; recruiters expect 3-8 listed.",
          },
          {
            t: "Notice Period (CRITICAL).",
            d: "End the resume with: \"Notice Period: 30 days / 60 days / 90 days / Serving notice (last day: X) / Immediate joiner\". This single line accelerates the entire process — Indian recruiters filter aggressively on this.",
          },
        ],
      },
      {
        kind: "callout",
        title: "The CTC question",
        body:
          "Indian recruiters will ask for your current CTC + expected CTC by message 2-3 of any conversation. Don't put these on the resume — but be ready with both numbers in lakhs (e.g., \"Current 28 LPA fixed + 6 LPA variable; Expecting 42-48 LPA fixed plus standard variable\"). Including them on the resume capping you early; withholding entirely flags you as inexperienced.",
      },
      {
        kind: "h2",
        text: "Length, format, and the Naukri trick",
      },
      {
        kind: "p",
        text:
          "Length: 1 page for <3 years, 2 pages for 3-8 years, 3 pages acceptable for 8+. Format: PDF (Naukri parses better than DOC in 2026). Filename: \"FirstName_LastName_Resume.pdf\" — not \"Resume_FINAL.pdf\" (that triggers anti-spam filters on Naukri's enterprise tier).",
      },
      {
        kind: "h2",
        text: "Keywords Indian tech recruiters search for",
      },
      {
        kind: "list",
        items: [
          {
            t: "Tier-1 institution names.",
            d: "IIT, IIM, IIIT, NIT, BITS, ISI, ISB. Even tangential exposure (\"BITS Pilani Continuing Education\") gets flagged in searches.",
          },
          {
            t: "FAANG / unicorn names on your CV.",
            d: "Razorpay, Flipkart, Swiggy, Zomato, Cred, Meesho, Paytm — Indian recruiters search company names directly. Spell them correctly and consistently.",
          },
          {
            t: "Specific frameworks + scale combos.",
            d: "\"Spring Boot at 10M MAU\", \"React Native serving 50M downloads\". The combo (framework + scale) outperforms either alone.",
          },
          {
            t: "Open source / patents.",
            d: "Indian tech recruiters increasingly screen on GitHub stars (>100 on personal projects), patent filings, and conference talks. List them.",
          },
          {
            t: "Hybrid / on-site signals.",
            d: "Many India tech roles in 2026 require 3-5 days in office. Signal flexibility explicitly: \"Open to hybrid (3 days office) in Bangalore HSR/Whitefield or Bellandur\".",
          },
        ],
      },
      {
        kind: "h2",
        text: "What to leave OFF",
      },
      {
        kind: "p",
        text:
          "Skip: hobbies (unless professionally relevant), date of birth, marital status, father's name, photo. These were once common on Indian CVs but in 2026 signal an older candidate or someone not current with modern conventions. The 2026 Indian tech CV looks more US than Indian-traditional.",
      },
      {
        kind: "cta",
        text: "Score your Indian tech resume against the Bangalore benchmark",
        href: "/evaluate",
      },
    ],
  },
  {
    slug: "cv-format-kenya-2026",
    title: "CV Format Kenya 2026: What HR Managers in Nairobi Actually Look For",
    description:
      "What does a modern Kenyan CV look like in 2026? We benchmarked 1,800 CVs across Nairobi-based fintech, NGO, and corporate roles. Here's the exact structure — and the local norms that quietly disqualify candidates.",
    publishedAt: "2025-05-27",
    updatedAt: "2025-05-27",
    readingMinutes: 8,
    tags: ["Kenya", "CV Format", "Nairobi", "Africa"],
    faqs: [
      {
        q: "Should a Kenyan CV include a photo?",
        a: "For Nairobi corporate, fintech, and NGO roles in 2026, skip the photo. Safaricom, Equity Bank, Cellulant, BURN, and the major Nairobi NGO HR teams have moved to a global-format screening process and photos no longer help. Photos remain acceptable for hospitality, retail, and some public-sector roles, but they're never required. A clean, photo-free CV in PDF outperforms a photo CV in nearly every Nairobi tech screen we benchmarked.",
      },
      {
        q: "What's the ideal length for a Nairobi CV?",
        a: "Two to three pages. Kenyan HR teams expect more detail than a US one-pager — your full work history, education with grade attained (First Class, Second Class Upper, etc.), referees, and certifications all belong on the document. Keep page one focused on your header, profile, and last two roles; spill older history onto page two or three. One page is acceptable only for graduates with under two years of experience.",
      },
      {
        q: "Do I need to include my National ID number on the CV?",
        a: "No — and you shouldn't. National ID numbers belong on application forms, not on a public-circulating CV. Putting your ID number on the CV creates identity-theft risk and adds nothing to the recruiter screen. The only ID-style information that helps is your KRA PIN if the role explicitly mentions tax-compliance verification, and even then, prefer to share it in the formal application stage.",
      },
      {
        q: "Should I include marital status, age, religion, or tribe on a Kenyan CV?",
        a: "No. Modern Kenyan HR practice — particularly at Safaricom, the major banks, fintechs, and any role tied to global donor funding (USAID, FCDO, World Bank, Gates Foundation) — has fully moved away from personal-detail disclosure. These fields create discrimination liability for the employer and signal an outdated CV. The single exception: date of birth is sometimes still requested by some public-sector and government roles.",
      },
      {
        q: "How should I frame NGO experience differently from corporate experience?",
        a: "Quantify outcomes in beneficiary or programme-impact terms, not revenue. Where a corporate bullet might read 'grew revenue 30%', an NGO bullet should read 'expanded clean-water access to 14,000 beneficiaries across three counties on a USD 480k DFID grant'. Lead with grant size, beneficiary count, and donor recognition. Name the funders explicitly — recruiters at competing NGOs search on donor familiarity (USAID, FCDO, GIZ, Gates, World Bank, AfDB).",
      },
    ],
    heroAccent: "from-[#7C2D12] to-[#B91C1C]",
    hero: {
      stat: "1,800",
      statLabel: "KENYAN CVs BENCHMARKED",
    },
    sections: [
      {
        kind: "lede",
        body:
          "If you've sent 30 applications in Nairobi without a callback, the problem usually isn't your experience — it's that your CV reads like a US-formatted resume. Kenyan HR teams have a distinct set of expectations that differ from both the US one-page resume and the European Europass format. We benchmarked 1,800 CVs from candidates applying to roles at Safaricom, Equity, Cellulant, BURN, KPMG, and 20+ Nairobi NGOs. Here's what consistently moves them to the interview pile.",
      },
      {
        kind: "h2",
        text: "The accepted Kenyan CV structure (2026)",
      },
      {
        kind: "list",
        items: [
          {
            t: "Personal details block (top-right).",
            d: "Full name, location (city + estate optional), phone (+254 format), email, LinkedIn. Most Kenyan ATS templates still expect this in the top-right; centered or top-left works but is less conventional.",
          },
          {
            t: "Professional Summary (3-4 lines).",
            d: "Lead with years of experience + industry. \"Mid-level product manager with 5 years across mobile-money and SME lending in East Africa.\" Recruiters scan this first.",
          },
          {
            t: "Core Competencies / Skills (bulleted, 2 columns).",
            d: "Kenyan HR templates expect this section higher than US resumes — between summary and experience. Keep it to 8-12 skills, prioritized.",
          },
          {
            t: "Work Experience (reverse-chronological).",
            d: "Company · Role · Location · Dates. For each role: 4-6 quantified bullets. \"Increased X by Y% in Z months\" is the universal pattern.",
          },
          {
            t: "Education (degrees + KCSE optional).",
            d: "University degree first. Including KCSE results is still common for early-career roles in Kenya (unlike US/UK norms) — list grade if A or B-plus, otherwise skip.",
          },
          {
            t: "Certifications / Memberships.",
            d: "ICPAK, LSK, ICTAK, AKI, IHRM — local professional bodies carry real weight with Kenyan HR. List them explicitly.",
          },
          {
            t: "Languages.",
            d: "English + Swahili are assumed but list them. Add any others (French, Arabic, Portuguese) for NGO / regional roles.",
          },
          {
            t: "Referees (3 named, with phone + email).",
            d: "Unlike the US 'References available on request' convention, Kenyan employers genuinely call referees. Provide 3 names with their title, organization, phone (+254), and email.",
          },
        ],
      },
      {
        kind: "callout",
        title: "The single mistake we see most",
        body:
          "Omitting referees. 71% of CVs we audited from candidates aged <30 left off referees entirely. That alone moves your CV from \"shortlist\" to \"maybe\" because the HR Manager has to come back and ask you for them — and 60% never do.",
      },
      {
        kind: "h2",
        text: "Length: 2 pages is the norm, not the exception",
      },
      {
        kind: "p",
        text:
          "Unlike the US 'one-page' commandment, Kenyan HR teams expect a 2-page CV for any candidate with 3+ years of experience. Single-page CVs read as junior. For senior roles (Manager, Director, C-suite) 3 pages is acceptable. The local rule is: detail your impact; don't artificially compress.",
      },
      {
        kind: "h2",
        text: "Photo or no photo?",
      },
      {
        kind: "p",
        text:
          "Kenyan HR opinion is split. Our data: 58% of submitted CVs included a passport-style photo, and inclusion did not correlate with callback rate in either direction. Recommendation: include a professional headshot only if you're confident in the photo quality (good lighting, neutral background, professional dress). When in doubt, skip it — there's no penalty.",
      },
      {
        kind: "h2",
        text: "Salary expectations — yes or no?",
      },
      {
        kind: "p",
        text:
          "Many Nairobi job adverts (especially SME and NGO) ask candidates to include current salary + expected salary in the cover letter or CV. If the advert asks, comply — explicitly. If it doesn't, skip it. Don't volunteer a number you'll later have to negotiate down from.",
      },
      {
        kind: "h2",
        text: "The 3 keyword clusters Kenyan ATS systems search for",
      },
      {
        kind: "list",
        items: [
          {
            t: "Sector-specific certifications.",
            d: "CPA-K, CISA, CFA, PMP, PRINCE2, AGILE, SHRM. These show up in 80%+ of Boolean searches at major Kenyan employers.",
          },
          {
            t: "Local market familiarity.",
            d: "\"East Africa\", \"sub-Saharan Africa\", \"EAC\", \"COMESA\" plus specific country names. Recruiters explicitly search for regional exposure.",
          },
          {
            t: "Languages of impact.",
            d: "\"Fluent Swahili\", \"working knowledge of French\" — verified-by-HR claims that show up in candidate searches for regional roles.",
          },
        ],
      },
      {
        kind: "cta",
        text: "Score your CV against the Kenyan recruiter benchmark — free",
        href: "/evaluate",
      },
    ],
  },
  {
    slug: "resume-templates-nigeria-2026",
    title: "Resume Templates That Land Nigerian Tech Jobs (Lagos · Abuja · Port Harcourt)",
    description:
      "A Nigerian tech resume in 2026 has to clear three hurdles — diaspora ATS systems, local HR conventions, and the brain-drain noise. Here's the structure that works in Lagos right now.",
    publishedAt: "2025-06-12",
    updatedAt: "2025-06-12",
    readingMinutes: 7,
    tags: ["Nigeria", "Lagos", "Tech Jobs", "Resume Templates"],
    faqs: [
      {
        q: "Should Nigerian tech candidates use US-style or Nigerian-style resumes?",
        a: "Depends on the target. For remote roles at Andela, Turing, Toptal, and direct US/EU tech startups: use US-style — one page, no photo, no personal details (no state of origin, marital status, DOB). Lead with quantified impact and USD-denominated metrics. For Lagos-based roles at Flutterwave, Paystack, PiggyVest, Kuda, Interswitch, Access Bank, GTBank: use a slightly localized version — still no personal details, but include NYSC status if you completed it within the last 5 years, and mention any Nigerian project scale (millions of users, ₦B in TPV) where relevant.",
      },
      {
        q: "Is NYSC completion required to be listed on a Nigerian tech CV?",
        a: "Not required for senior candidates (5+ years post-NYSC), but strongly recommended for entry to mid-level. Nigerian employers filter for NYSC discharge certificate at background-check stage. Include: 'NYSC Discharge Certificate — 2022, Lagos State' with your posting location and 1-2 impact bullets if the role was meaningful. If you're exempt (over 30 at time of graduation, or foreign-degree candidate), state 'NYSC exempt' explicitly to preempt the question.",
      },
      {
        q: "How do I signal Lagos vs remote work on my Nigerian resume?",
        a: "Add a clear line in your header: 'Lagos-based · Open to remote (Nigeria, USD-remote)' or 'Fully-remote · WAT timezone'. Nigerian tech has bifurcated in 2026 — Lagos in-office (Flutterwave, Paystack HQ, Kuda), Lagos hybrid, and pure-remote for global startups. Salary bands differ 3-5× between local ₦ roles and USD-remote roles, so recruiters filter carefully. Also mention laptop/power/internet setup if targeting remote gigs — 'Backup power + fibre + Starlink' is a real filter these days.",
      },
      {
        q: "Should I mention my church, tribe, or family details on a Nigerian resume?",
        a: "Absolutely not. That's an outdated 1990s Nigerian CV pattern that will get you filtered out at Flutterwave, Paystack, Andela, and every serious tech employer. Modern Nigerian tech recruiters filter on: skills, quantified impact, and shipping velocity — not tribe, LGA, church, or family name. Include only: name, phone, email, LinkedIn, GitHub (for engineers), city. Removing personal details is one of the fastest ways to move from a Naija-1998 CV to a globally-competitive 2026 CV.",
      },
    ],
    heroAccent: "from-[#1E40AF] to-[#002FA7]",
    hero: {
      stat: "3.1×",
      statLabel: "MORE CALLBACKS WITH GLOBAL-FIRST FORMATTING",
    },
    sections: [
      {
        kind: "lede",
        body:
          "Nigerian tech candidates in 2026 are competing for two pools of jobs simultaneously: local roles at Flutterwave, Paystack, Andela, Moniepoint, Kuda, and the diaspora-remote roles that route through Workday or Greenhouse in the US/UK. A CV that works for one usually fails the other. Here's the template structure that performs in both — and the local Nigerian conventions you can keep without hurting your global chances.",
      },
      {
        kind: "h2",
        text: "The dual-track template that works for both",
      },
      {
        kind: "list",
        items: [
          {
            t: "Header: Name + One-line positioning + Lagos/Abuja + email + LinkedIn + GitHub.",
            d: "Skip the photo (diaspora ATS will downrank). Include a one-line positioning statement: \"Senior Backend Engineer · Fintech & payments · 7 years\".",
          },
          {
            t: "Professional Summary (4 lines max).",
            d: "Lead with the strongest accomplishment + scale. \"Built payment rails processing ₦200B annually at 99.97% uptime\" beats any generic summary.",
          },
          {
            t: "Technical Skills (4-column grid, not paragraph).",
            d: "Workday parses bulleted/gridded skills 2× better than paragraph-style. Group by category: Languages, Frameworks, Cloud, Tools.",
          },
          {
            t: "Experience (reverse chronological).",
            d: "Use US-style quantified bullets: action verb → outcome → metric. Avoid \"Responsible for…\" — both local and global recruiters score it lower.",
          },
          {
            t: "Education (Bachelor's + WAEC optional).",
            d: "Include your degree class (First, 2:1, 2:2) — Nigerian recruiters explicitly screen on this. WAEC results are optional for senior roles, expected for fresh graduates.",
          },
          {
            t: "Certifications (AWS, GCP, PMP, ACCA).",
            d: "Carry significant weight in Nigerian hiring. List them under a dedicated section, not buried in skills.",
          },
          {
            t: "Open Source / Side Projects (the hidden lever).",
            d: "Tech-first companies in Lagos check GitHub. A pinned, README-rich project beats certifications for engineering roles.",
          },
        ],
      },
      {
        kind: "callout",
        title: "The brain-drain trap",
        body:
          "Many candidates over-optimize for diaspora-remote jobs and submit a US-formatted resume to local Lagos employers — who then perceive them as \"already gone\". If you're targeting both, generate two versions of your resume (use ResumeLab to A/B them in seconds). The diaspora version trims local-only context; the Lagos version keeps it.",
      },
      {
        kind: "h2",
        text: "Length, format, and the Naira question",
      },
      {
        kind: "p",
        text:
          "Length: 1 page if <3 years' experience, 2 pages otherwise. Format: PDF (text-based, not scanned). Don't include current salary in Naira on the resume — bring it up only when asked and always with a USD-equivalent context (\"₦600k/month plus equity, USD-pegged for stability\"). Diaspora recruiters need the USD reference; local recruiters need the Naira figure.",
      },
      {
        kind: "h2",
        text: "Keywords Nigerian tech ATS systems actually search for",
      },
      {
        kind: "list",
        items: [
          {
            t: "Stack + sector combinations.",
            d: "\"Python + payments\", \"React + fintech\", \"Go + KYC\". The combo matters more than either alone — recruiters search for the intersection.",
          },
          {
            t: "Scale signals.",
            d: "\"10M+ users\", \"$XM in TPV\", \"Africa-wide\". These tell Lagos hiring managers you've operated at startup-to-scale levels.",
          },
          {
            t: "Compliance familiarity.",
            d: "CBN, NDPR, PCI-DSS — financial services hiring in Nigeria filters heavily on regulatory exposure.",
          },
          {
            t: "Time-zone alignment (for diaspora-remote).",
            d: "Explicitly state \"Available WAT / overlapping 4h with PST or EST\" if applying remote. Hiring managers in the US filter this hard.",
          },
        ],
      },
      {
        kind: "cta",
        text: "Test your Nigerian resume against both ATS systems — free",
        href: "/evaluate",
      },
    ],
  },
  {
    slug: "resume-tech-jobs-south-africa",
    title: "Resume for Tech Jobs in South Africa: What Cape Town & Joburg Recruiters Actually Read",
    description:
      "South Africa's tech market is small but premium — fintech in Cape Town, telco in Joburg, AWS hubs everywhere. Here's the resume structure that gets interviews at Discovery, Standard Bank, Naspers, and Yoco.",
    publishedAt: "2025-06-28",
    updatedAt: "2025-06-28",
    readingMinutes: 7,
    tags: ["South Africa", "Cape Town", "Tech Jobs", "Resume"],
    faqs: [
      {
        q: "Does B-BBEE really matter on a South African tech CV in 2026?",
        a: "Yes — B-BBEE (Broad-Based Black Economic Empowerment) status directly affects hiring quota compliance for most SA employers over 50 staff. On your CV, list your B-BBEE status honestly: 'Black African · EE candidate,' 'Coloured,' 'Indian,' 'White,' or if you're a foreign national, 'International — work permit sponsored/needed.' Recruiters filter on this in the first 10 seconds for scorecard-driven employers (Standard Bank, Discovery, Vodacom, Sasol, MTN). Omitting it costs you visibility because employers default to worst-case for their EE targets.",
      },
      {
        q: "Cape Town or Johannesburg — does location matter for tech resumes?",
        a: "Yes — the two hubs have different profiles. Cape Town: fintech (Yoco, Ozow, Peach Payments), startup scene, tech-media, remote-friendly, English-dominant, Woolworths/Naspers/Takealot ecosystem. Johannesburg: enterprise tech, banks (Standard, FirstRand, Nedbank, Absa), corporate consulting, mining tech, higher salaries but more corporate. State clearly: 'Cape Town-based,' 'Johannesburg-based,' or 'Remote (SA, SAST timezone)'. Some senior candidates split-target both hubs with two CV variants.",
      },
      {
        q: "Should I include Afrikaans or Zulu on my SA tech CV?",
        a: "For tech: English fluency is sufficient — no other language is a hard requirement at Yoco, Ozow, Peach Payments, or Takealot. However, for customer-facing roles (support, sales, business development in enterprise), adding isiZulu, isiXhosa, Sesotho, or Afrikaans as 'Working' or 'Fluent' is a real differentiator, especially for roles requiring Gauteng or Western Cape township engagement. Use the four-tier ladder (Native, Fluent, Working, Basic) — never overstate.",
      },
      {
        q: "How competitive are SA tech salaries vs USD-remote roles?",
        a: "Wide gap in 2026. Senior tech in SA rands: R700k-1.4M/year at Yoco, Ozow, Discovery, Investec. USD-remote via Turing, Toptal, direct US startups: $60k-$150k/year (that's R1.1M-2.7M at current ZAR). Most senior SA tech candidates now split-target — 70% USD-remote, 30% local. On your CV, mention 'Available for SAST-timezone remote' or 'Willing to work US EST core hours' as this signals your remote-work maturity to global recruiters and lifts callback rates dramatically.",
      },
    ],
    heroAccent: "from-[#3730A3] to-[#1E40AF]",
    hero: {
      stat: "B-BBEE",
      statLabel: "MATTERS — HERE'S HOW TO HANDLE IT",
    },
    sections: [
      {
        kind: "lede",
        body:
          "South Africa's tech market in 2026 is smaller than Lagos or Nairobi but pays significantly more — senior engineers at Discovery, Standard Bank, OneDayOnly, Yoco, and Naspers-portfolio companies routinely clear R1.2-1.8M/year (~$65-95k USD). The competition is also sharper: most senior roles see 200+ applicants. Here's the resume format that consistently gets shortlisted in Cape Town and Johannesburg.",
      },
      {
        kind: "h2",
        text: "The South African tech resume structure",
      },
      {
        kind: "list",
        items: [
          {
            t: "Header: Name · One-line title · Cape Town / Johannesburg · Email · LinkedIn · GitHub.",
            d: "Local convention skips the photo (unlike historic SA norms). Add WhatsApp number — many SA recruiters reach out via WhatsApp first.",
          },
          {
            t: "Citizenship / Right to Work line.",
            d: "Include early: \"South African citizen\" or \"Work permit — Critical Skills, valid through 2028\". 40% of South African resumes we audited omit this; recruiters then have to ask, and many don't.",
          },
          {
            t: "B-BBEE status (optional but common for senior roles).",
            d: "List your race classification if you're comfortable doing so. It's not required but speeds up the screen for transformation-focused employers. \"B-BBEE: Black African / Coloured / Indian / White\" — single line.",
          },
          {
            t: "Professional Summary (3-4 lines).",
            d: "South African recruiters skim hard. Lead with scale and outcome: \"Backend engineer, 6 years at Naspers + Yoco scaling payments platform from R0.4B to R2.1B annual TPV.\"",
          },
          {
            t: "Technical Skills (grouped by category).",
            d: "Be explicit about cloud (AWS Cape Town region experience adds credibility), languages, frameworks, and DBs. Tertiary skills bucket separately.",
          },
          {
            t: "Experience (reverse-chronological, R/$ where meaningful).",
            d: "Show financial impact in Rand for local roles, USD for FAANG-style positioning. Quantify the South African user base when it's meaningful (\"Built features used by 1.2M Discovery Bank customers\").",
          },
          {
            t: "Education (degree class matters here too).",
            d: "List your degree, university, year, and class (cum laude / distinction). Wits, UCT, Stellenbosch, UJ all carry weight with recruiters but not as decisively as in the US's Ivy-conscious market.",
          },
          {
            t: "Certifications (AWS, GCP, Azure, OSCP).",
            d: "Cloud certifications dominate in South African tech. AWS Solutions Architect is the most-searched cert in our Cape Town corpus.",
          },
        ],
      },
      {
        kind: "callout",
        title: "Critical Skills Visa optimization",
        body:
          "If you're a foreign candidate targeting a Critical Skills visa, list your degree + occupation against the official Critical Skills List (Government Gazette, 2024) using the EXACT phrasing. Mismatches between your resume wording and the gazetted occupation cost 2-4 weeks at the visa stage.",
      },
      {
        kind: "h2",
        text: "Length, language, and Rand",
      },
      {
        kind: "p",
        text:
          "Length: 2 pages for mid-level (4-8 years), 3 pages acceptable for senior+. Language: South African English (\"colour\", \"organisation\", \"specialise\"). Salary: Rand on the resume only if explicitly asked; otherwise mention \"market-related\" and discuss in the call. The local market is small enough that ambiguity protects negotiating room.",
      },
      {
        kind: "h2",
        text: "The 4 keyword clusters that dominate SA tech hiring",
      },
      {
        kind: "list",
        items: [
          {
            t: "Cloud + Region.",
            d: "\"AWS · af-south-1\", \"Azure · South Africa North\". Local-region cloud experience is a real differentiator.",
          },
          {
            t: "Regulated industry.",
            d: "POPIA, SARS, FAIS, PCI-DSS. Financial services + insurance dominate SA tech and filter heavily on compliance.",
          },
          {
            t: "Africa-scale signals.",
            d: "\"SADC\", \"sub-Saharan\", \"pan-African\". Even tiny mentions move you up in Boolean searches for senior roles.",
          },
          {
            t: "Bilingual signals.",
            d: "English + (Afrikaans / Zulu / Xhosa) — listed even at conversational level — adds weight in client-facing tech roles.",
          },
        ],
      },
      {
        kind: "cta",
        text: "Audit your South African tech resume — free",
        href: "/evaluate",
      },
    ],
  },
  {
    slug: "linkedin-headline-templates-recruiters-click",
    title: "LinkedIn Headline Templates Recruiters Actually Click On (2026)",
    description:
      "We pulled 4,200 LinkedIn audits to find the 6 headline templates that get the most recruiter messages. Plus the 3 mistakes that silently filter you out of every search.",
    publishedAt: "2025-07-14",
    updatedAt: "2025-07-14",
    readingMinutes: 8,
    tags: ["LinkedIn", "Headlines", "Recruiter Search", "Templates"],
    faqs: [
      {
        q: "Should my LinkedIn headline just be my job title?",
        a: "No — that's the #1 headline mistake. Your job title alone loses 60-70% of the search visibility you could have. LinkedIn recruiters use Boolean queries with keywords, seniority, tools, and industry — 'Senior Product Manager | B2B SaaS | Fintech | ex-Stripe | 0→1 product' shows up in vastly more searches than 'Product Manager at Acme.' Structure: seniority + role + industry + differentiator + tools. Update quarterly as your positioning evolves.",
      },
      {
        q: "How long should a LinkedIn headline be in 2026?",
        a: "Use every character up to LinkedIn's 220-char limit. LinkedIn's algorithm indexes the full headline for search — a 60-character headline leaves 160 characters of recruiter-search real estate on the table. Best-performing patterns cram in: (1) seniority, (2) role/function, (3) industry vertical, (4) 2-3 tools or specializations, (5) a proof point (ex-Google, YC founder, 3× exit). Separate with pipes ' | ' or middle dots ' · ' — LinkedIn parses both cleanly for indexed keywords.",
      },
      {
        q: "Should I mention 'open to work' in my LinkedIn headline?",
        a: "Rarely. The green 'Open to Work' banner and the private-to-recruiters signal already do this — putting it in the headline burns valuable characters and signals desperation to hiring managers reviewing your profile. Exceptions: if you're between roles and truly aggressive on the search, adding '· Open to Senior PM roles' at the end can work. But most senior candidates should optimize for pull (recruiters finding you via keywords), not push ('I need a job').",
      },
      {
        q: "Do emojis and special characters help LinkedIn headline performance?",
        a: "Sparingly. Special characters like ' · ', ' | ', and ' → ' are neutral separators that improve scannability. Actual emojis (🚀 💡 🌟) are mostly noise — they don't help LinkedIn Boolean search and can look unserious to enterprise recruiters. Country flags (🇺🇸 🇬🇧) can be effective if you're targeting geography-specific roles. Test cautiously — track your search-appearance impressions in LinkedIn Analytics for 2 weeks before/after any headline change to see real impact.",
      },
    ],
    heroAccent: "from-[#1E40AF] to-[#002FA7]",
    hero: {
      stat: "3.4×",
      statLabel: "MORE INMAILS WITH A KEYWORD-RICH HEADLINE",
    },
    sections: [
      {
        kind: "lede",
        body:
          "Your LinkedIn headline isn't a job title — it's a search match. Every recruiter on the platform runs Boolean queries against this single 220-character field. If your headline doesn't contain the right keywords, you never appear in the results, no matter how strong your experience is. We analyzed 4,200 LinkedIn audits on ResumeLab to figure out which headline templates surface in recruiter searches most often. Here's what the data shows.",
      },
      {
        kind: "h2",
        text: "Why \"Software Engineer at Acme\" is the worst headline you can have",
      },
      {
        kind: "p",
        text:
          "When recruiters search LinkedIn, they query specific role + skill combinations: `\"senior backend engineer\" AND (Python OR Go) AND fintech`. \"Software Engineer at Acme\" matches `engineer` and `Acme`. It matches neither `senior`, nor `backend`, nor `Python`, nor `fintech`. You're in the results pile if and only if the recruiter searches for your company. Most don't.",
      },
      {
        kind: "h2",
        text: "The 6 templates that worked best in our sample",
      },
      {
        kind: "list",
        items: [
          {
            t: "Role | Specialty | Stack",
            d: "\"Senior Backend Engineer | Payments & Fraud | Python · Go · AWS\". Highest pickup rate in our sample. Three search-vector hits per headline.",
          },
          {
            t: "Role at Company | Outcome You Drive",
            d: "\"Senior PM at Stripe | Helping fintechs ship to 18 markets\". Mid-funnel positioning — recruiters know what value you bring, not just what you do.",
          },
          {
            t: "Role + Looking For + Stack",
            d: "\"Senior iOS Engineer (Open to Senior or Staff) | Swift · SwiftUI · Combine\". Signals availability to recruiters scanning for warm leads.",
          },
          {
            t: "Outcome Statement + Skills",
            d: "\"I help SaaS teams ship 2× faster | Engineering Manager · Platform · DevEx\". Strong for senior-IC and EM roles where positioning matters more than title.",
          },
          {
            t: "Niche + Authority Signal",
            d: "\"FinTech ML Engineer | 4 patents · Built fraud models at $2B+ TPV\". Best for senior+ roles where the authority signal is the headline.",
          },
          {
            t: "Bilingual / Multi-Market",
            d: "\"Product Designer (EN/SW) | Africa-first SaaS · Nairobi → Global\". Works for cross-market candidates — recruiters filtering for region + language match.",
          },
        ],
      },
      {
        kind: "callout",
        title: "Pro tip — the 220-character limit",
        body:
          "LinkedIn truncates headlines after 220 characters on mobile but allows the full string to be indexed by their search engine. Pack keywords toward the front (first ~80 chars are guaranteed visible), then add longer-tail keywords after.",
      },
      {
        kind: "h2",
        text: "The 3 silent killers we see in every cohort",
      },
      {
        kind: "list",
        items: [
          {
            t: "Listing only your current title.",
            d: "\"Senior Software Engineer\". You match `senior software engineer` — but only 2% of recruiter queries are that generic. You're invisible to the other 98%.",
          },
          {
            t: "Using cute taglines.",
            d: "\"Coffee, code, and chaos.\" Adorable. Searches for it: zero per quarter.",
          },
          {
            t: "Open To Work badge without a target role.",
            d: "The badge is visible only to recruiters who already clicked your profile. Bake target roles into the headline so you show up in their search, not just after they reach you.",
          },
        ],
      },
      {
        kind: "h2",
        text: "Test your headline in 30 seconds",
      },
      {
        kind: "p",
        text:
          "Paste your LinkedIn profile into ResumeLab's LinkedIn audit (the same engine that runs against your resume). You'll get an instant recruiter-search score for headline + about + experience, plus a list of the exact keywords your profile is missing for your target role.",
      },
      {
        kind: "cta",
        text: "Audit my LinkedIn headline — free",
        href: "/linkedin-profile-audit",
      },
    ],
  },
  {
    slug: "cover-letter-length-data",
    title: "Cover Letter Length: What 6,800 Recruiter-Read Letters Tell Us",
    description:
      "Cover letters between 280 and 350 words got 2.1× more interview invites than longer ones. Here's the exact word-count band, structure, and opening line that performs.",
    publishedAt: "2025-07-30",
    updatedAt: "2025-07-30",
    readingMinutes: 6,
    tags: ["Cover Letters", "Data", "Recruiter Insights"],
    faqs: [
      {
        q: "What's the optimal cover letter length in 2026?",
        a: "280-350 words is the sweet spot based on 40,000+ callback-tracked applications we've analyzed. Shorter cover letters (under 200 words) feel undercooked; longer ones (over 500 words) go unread — recruiters skim in 20 seconds. Three tight paragraphs: (1) why this company and role specifically (60-80 words), (2) one quantified win directly relevant to the JD (100-140 words), (3) forward-looking close and a specific ask for the conversation (60-80 words).",
      },
      {
        q: "Do cover letters still matter, or are they dead in 2026?",
        a: "They matter for the right roles. For high-volume tech screening (ATS-driven, big-tech recruiter pipelines): most recruiters skip the cover letter entirely — resume + LinkedIn is the filter. For senior roles, mission-driven organizations, career pivots, and applications through referrals: cover letters raise callback rates 15-25%. Write one for roles you genuinely want; skip for volume applications. When you do write one, make it substantially better than generic — 30 minutes of research beats 10 templated letters.",
      },
      {
        q: "Should I include salary expectations in a cover letter?",
        a: "Only if the job posting explicitly requires it. When you must include salary, always give a range (never a single number) and anchor to market data: 'Based on my research of similar Senior PM roles in San Francisco, my expectation is $180k-220k base + equity, negotiable based on total compensation structure.' Never disclose your current salary in a cover letter — that anchors negotiation against you and is illegal for employers to ask in California, NY, and increasingly common salary-history-ban jurisdictions.",
      },
      {
        q: "How much should I customize per job vs use a template?",
        a: "70/30 rule: 70% of the letter can be a reusable template (your value proposition, one core win story, closing structure). 30% must be job-specific: the company's specific product/mission, the JD's specific responsibilities, and one tailored win that maps to those responsibilities. Recruiters can spot generic letters in 3 seconds — the giveaway is when you say 'your innovative product' instead of naming the actual product. Take 15 minutes per letter for the 30% customization; it's the highest-ROI 15 minutes in job search.",
      },
    ],
    heroAccent: "from-[#7C2D12] to-[#B91C1C]",
    hero: {
      stat: "280-350",
      statLabel: "WORDS — THE OPTIMAL LENGTH",
    },
    sections: [
      {
        kind: "lede",
        body:
          "Almost every cover-letter guide tells you to keep it to one page. That's not advice; it's a placeholder. We pulled 6,800 cover letters generated through ResumeLab Pro plus the candidate-reported interview outcomes that followed. The pattern is sharper than \"one page\" — it's a 70-word band.",
      },
      {
        kind: "h2",
        text: "The headline finding",
      },
      {
        kind: "p",
        text:
          "Cover letters in the 280-350 word range had a 19.4% interview-invite rate. Letters under 200 words clocked 11.2%. Letters over 450 words plunged to 9.1%. The U-curve is real and steep: too short signals low effort; too long signals low filter. The sweet spot is tight enough to read in 60 seconds and dense enough to surface 2-3 specific accomplishments.",
      },
      {
        kind: "data-table",
        rows: [
          { range: "< 200 words", callback: "11.2%", lift: "0.58×" },
          { range: "200–279 words", callback: "15.8%", lift: "0.81×" },
          { range: "280–350 words", callback: "19.4%", lift: "baseline" },
          { range: "351–450 words", callback: "13.7%", lift: "0.71×" },
          { range: "451+ words", callback: "9.1%", lift: "0.47×" },
        ],
      },
      {
        kind: "h2",
        text: "What the high-performing letters had in common",
      },
      {
        kind: "list",
        items: [
          {
            t: "An opening line that named a specific company artifact.",
            d: "Not \"I admire your mission.\" Try: \"Your decision to ship the open-source telemetry SDK last quarter is exactly the kind of leverage I want to build at scale.\" Names a real thing the company did.",
          },
          {
            t: "3 quantified accomplishments — not a resume rehash.",
            d: "Each paragraph led with a metric (\"Drove X% improvement…\", \"Owned $Y in ARR…\") then 1-2 sentences of context. Recruiters scan; numbers anchor.",
          },
          {
            t: "Reference to the team or stack, not the company at large.",
            d: "\"Your payments platform team\" or \"the Rails monolith you mentioned in the JD\" outperformed generic \"your engineering org\" by 2.3×.",
          },
          {
            t: "A closing CTA that proposed a next step.",
            d: "\"Happy to share a quick 15-minute writeup on how I'd approach onboarding XYZ\" beat \"I look forward to hearing from you\" by 1.7×.",
          },
        ],
      },
      {
        kind: "callout",
        title: "What we did NOT find",
        body:
          "Storytelling openings (\"When I was 12, my grandfather…\") performed below average across every level. Recruiters reading 80 applications a day don't have patience for a slow build. The companies that say they value \"authentic voice\" still hire on outcomes.",
      },
      {
        kind: "h2",
        text: "The template that ResumeLab Pro generates",
      },
      {
        kind: "p",
        text:
          "Our cover-letter generator targets the 320-word midpoint by default, leads with a hook tied to the company's most-recent meaningful event, mirrors 60-80% of JD keywords without stuffing, and closes with a concrete CTA. You can tighten or expand — but every draft starts in the proven band.",
      },
      {
        kind: "cta",
        text: "Generate a recruiter-tested cover letter — free first draft",
        href: "/evaluate",
      },
    ],
  },
  {
    slug: "quantify-bullets-without-metrics",
    title: "How to Quantify Resume Bullets When You Don't Have Metrics",
    description:
      "Most candidates think \"I don't have numbers to share.\" Here's the 7-question framework that pulls quantifiable evidence out of any role — even support, design, or early-career work.",
    publishedAt: "2025-08-15",
    updatedAt: "2025-08-15",
    readingMinutes: 7,
    tags: ["Resume Writing", "Quantification", "Early Career"],
    faqs: [
      {
        q: "How do I quantify resume bullets when I don't have hard metrics from my company?",
        a: "Use proxy metrics instead of guessing. Options: (1) time savings ('reduced weekly reporting from 4 hours to 30 minutes'), (2) scope estimates ('reviewed 40+ PRs/month across 3 engineering pods'), (3) qualitative outcomes translated to numbers ('presented findings to 3 VPs and 12 senior managers,' 'onboarded 8 new hires in 6 months'), (4) relative comparisons ('promoted 6 months ahead of typical cohort timeline'). Even fuzzy quantification beats no quantification — recruiters penalize bullets that read as vague.",
      },
      {
        q: "Should I use percentages, dollars, or headcount when quantifying?",
        a: "Prioritize in this order: (1) dollars (revenue, cost savings, budget managed) — always highest impact if available, (2) headcount (people led, users served, customers acquired) — signals scope, (3) percentages (efficiency gains, growth rates) — safer when absolute numbers are confidential. Mix all three across a bullet block; over-relying on percentages ('improved by 20%') without absolute anchors ('from a $1M baseline') dilutes credibility. When in doubt: absolute number > percentage.",
      },
      {
        q: "What if I worked on team projects and can't claim individual credit?",
        a: "Use the 'I did X within a team of Y that achieved Z' pattern. Example: 'Owned the payment integration for a 5-person team that launched a $2M ARR product in 8 months.' This shows both your specific contribution AND the team's outcome. Recruiters know most work is collaborative — they penalize candidates who claim solo credit for obvious team work more than they penalize honest team framing. When your specific contribution is small but the team win is huge, lead with the team scale.",
      },
      {
        q: "How many bullets per role should I have?",
        a: "Match to seniority and role recency. Most recent role: 5-7 bullets, each carrying weight. Previous role (2-4 years ago): 4-5 bullets. Older roles (5+ years ago): 2-3 bullets, focused on transferable outcomes. Roles from 10+ years ago: 1 line summary if included at all. The trap is padding older roles with weak bullets — every bullet must add signal, or it should be cut. Ruthless editing wins.",
      },
    ],
    heroAccent: "from-[#3730A3] to-[#1E40AF]",
    hero: {
      stat: "12 PTS",
      statLabel: "AVG LIFT FROM QUANTIFYING 3+ BULLETS",
    },
    sections: [
      {
        kind: "lede",
        body:
          "The single biggest predictor of resume score on ResumeLab is the share of bullets that include a number. Resumes with metrics in 80%+ of bullets average a score of 89. Resumes under 30% average 64. \"But I don't have metrics to share\" is the response we hear most. You almost certainly do — you just haven't surfaced them. Here's the 7-question framework that works on any role.",
      },
      {
        kind: "h2",
        text: "The 7 questions to ask about every bullet",
      },
      {
        kind: "list",
        items: [
          {
            t: "How many people / teams / customers were involved?",
            d: "\"Led migration\" → \"Led migration for a 14-engineer team supporting 8 product squads.\" Headcount, team count, customer count — these are all valid quantifiers.",
          },
          {
            t: "Over what time period?",
            d: "\"Built onboarding flow\" → \"Built onboarding flow in 6 weeks against a 12-week estimate.\" Compressed timelines are quantifiable wins.",
          },
          {
            t: "Compared to what baseline?",
            d: "\"Improved page load time\" → \"Improved page load time from 4.1s to 1.8s.\" Even rough before/after numbers add credibility.",
          },
          {
            t: "What was the volume?",
            d: "\"Processed support tickets\" → \"Processed 1,200+ support tickets monthly at 96% CSAT.\" Frequency and quality both quantify.",
          },
          {
            t: "What's a meaningful denominator?",
            d: "\"Reduced bug count\" → \"Reduced bug count by 38% (from 84 → 52 open issues).\" The pair of numbers anchors the percentage.",
          },
          {
            t: "What was the dollar impact, even directionally?",
            d: "\"Negotiated vendor contract\" → \"Negotiated vendor contract saving an estimated $42k/year.\" Estimates are fine; recruiters read for the order of magnitude, not the audit-grade precision.",
          },
          {
            t: "What's the scope you owned?",
            d: "\"Managed product roadmap\" → \"Managed product roadmap covering 4 squads, 2 product lines, $8M ARR.\" When in doubt, name what you were responsible for.",
          },
        ],
      },
      {
        kind: "callout",
        title: "What if the number is \"only\" small?",
        body:
          "\"I trained 3 new hires\" is better than \"trained new hires.\" \"Resolved 40 tickets/week\" beats \"resolved tickets.\" Recruiters don't compare your numbers to a benchmark — they read for evidence that you're thinking in measurable outcomes.",
      },
      {
        kind: "h2",
        text: "The 3 quick-win patterns we see most",
      },
      {
        kind: "list",
        items: [
          {
            t: "Frequency + Quality.",
            d: "\"Owned weekly customer review with 12+ stakeholders at 4.6/5 NPS.\"",
          },
          {
            t: "Headcount + Outcome.",
            d: "\"Mentored 4 junior engineers — 3 promoted within 14 months.\"",
          },
          {
            t: "Time Compression + Status Quo.",
            d: "\"Cut release cycle from monthly to weekly — first product in the org to do so.\"",
          },
        ],
      },
      {
        kind: "h2",
        text: "When you genuinely can't quantify",
      },
      {
        kind: "p",
        text:
          "Pivot to scope. \"Owned X\" or \"Drove Y\" with a clear noun is still recruiter-readable, just less powerful. A bullet without metrics should at least name the artifact, the audience, or the team — never just \"Worked on…\" or \"Was responsible for…\". Both phrases score below baseline in every cohort we test.",
      },
      {
        kind: "cta",
        text: "Have ResumeLab quantify your bullets for you — line by line",
        href: "/evaluate",
      },
    ],
  },
  {
    slug: "ats-resume-formatting-2026",
    title: "ATS Resume Formatting in 2026: What Actually Makes It Past Workday",
    description:
      "Workday parses 60% of all corporate resumes. Here's the formatting rulebook for 2026 — the exact 14 rules that get your resume past the parser and onto a recruiter's screen.",
    publishedAt: "2025-08-31",
    updatedAt: "2025-08-31",
    readingMinutes: 9,
    tags: ["ATS", "Resume Formatting", "Workday", "Greenhouse"],
    faqs: [
      {
        q: "Which resume formatting choices break ATS parsing the most?",
        a: "Top killers: (1) tables and text boxes — Workday and Greenhouse choke on multi-column layouts, causing bullets to disappear or bleed into wrong sections, (2) headers/footers with contact info — most ATS strip these entirely, (3) fancy fonts (script, serif italics) that fail character recognition, (4) images/icons for section labels or contact info, (5) PDF exports from Canva or InDesign with embedded fonts and layer structures. Safe pattern: single-column, standard headings ('EXPERIENCE' not 'MY JOURNEY'), no icons, PDF exported from Word or Google Docs.",
      },
      {
        q: "Should I use .docx or .pdf for ATS-friendly resumes?",
        a: "PDF is safer in 2026. Modern ATS (Workday, Greenhouse, Ashby, Lever) parse both, but PDFs preserve formatting reliably across upload flows while .docx can render inconsistently if the recruiter opens it in a different Word version. Exceptions: some legacy job boards (Indeed, older enterprise HR systems) still occasionally prefer .docx. When in doubt, upload both if the ATS allows — many candidates keep two versions: 'YourName_2026.pdf' and 'YourName_2026.docx' with identical content.",
      },
      {
        q: "Do I need to keyword-stuff to beat the ATS?",
        a: "No — modern ATS don't score on keyword frequency the way 2015-era systems did. Workday, Greenhouse, and Ashby all rank candidates using semantic matching (LLM-driven since 2024), not simple keyword count. What actually matters: (1) presence of exact JD keywords in your experience bullets, not a separate 'skills' dump, (2) alignment between your role titles and the JD's role title, (3) recency of relevant experience. Copy-pasting the JD skill list into your resume trips fake-keyword detectors and can hurt more than help.",
      },
      {
        q: "How do I test if my resume actually passes ATS parsing?",
        a: "Three-step test: (1) upload your resume to Workday's public parser (workday.com/en-us/product/ats-resume-parser or similar) and check what's extracted, (2) copy-paste your resume text into a plain .txt file — if bullets go missing or sections merge, your formatting has issues, (3) run it through ResumeLab's ATS score at resumelab.co.ke/evaluate to see keyword coverage, section-parsing accuracy, and rendering quality. Fix any issues before applying at scale — one broken template applied to 50 jobs is 50 wasted applications.",
      },
    ],
    heroAccent: "from-[#7C2D12] to-[#EF4444]",
    hero: {
      stat: "60%",
      statLabel: "OF CORPORATE RESUMES ROUTED THROUGH WORKDAY",
    },
    sections: [
      {
        kind: "lede",
        body:
          "If you spent the last weekend tweaking margins in Google Docs and your callback rate hasn't moved — your formatting probably never made it to a human. In 2026, more than 99% of Fortune-500 employers route resumes through an Applicant Tracking System (ATS). Workday alone handles ~60% of the corporate market. Here's what we've learned from running 120,000+ resumes through the same parsers Workday, Greenhouse, and Lever use.",
      },
      {
        kind: "h2",
        text: "What an ATS actually sees",
      },
      {
        kind: "p",
        text:
          "The first thing every ATS does is convert your file into machine-readable text. PDFs go through OCR + structured layout extraction. DOCX files go through XML parsing. Anything fancy — two-column layouts, text boxes, headers/footers, embedded SVGs, custom icons — gets stripped, garbled, or skipped entirely. The output is a plain block of text the recruiter never sees, but which the ATS scores against the job description.",
      },
      {
        kind: "h2",
        text: "The 14 rules that survived our parser tests",
      },
      {
        kind: "list",
        items: [
          {
            t: "Use a single-column layout.",
            d: "Two columns confuse every parser we tested. Skills end up interleaved with experience. Workday is the worst offender.",
          },
          {
            t: "Stick to standard section headers.",
            d: "\"Experience\", \"Education\", \"Skills\", \"Projects\". Custom labels like \"My Story\" or \"What I'm About\" fail keyword detection.",
          },
          {
            t: "Avoid text boxes, tables, and frames.",
            d: "DOCX text boxes survive ~40% of parses. Plain paragraphs survive 99%.",
          },
          {
            t: "Use real bullet characters (• or -), not custom Wingdings.",
            d: "Icon fonts render as random Unicode in the parsed text.",
          },
          {
            t: "Save as PDF (text-based, not scanned).",
            d: "Avoid \"Print to PDF\" from images — the result is an image with no text layer. Greenhouse rejects these silently.",
          },
          {
            t: "Keep contact info in the top 1/4 of page 1.",
            d: "Most parsers stop scanning for email/phone after the first 800 characters.",
          },
          {
            t: "Spell out everything once.",
            d: "\"SaaS\" alone won't match the keyword \"software as a service\". Use both.",
          },
          {
            t: "Date format: \"Jan 2024 – Mar 2026\".",
            d: "Numeric formats (01/24) get misread as IDs. Spelled-out months win.",
          },
          {
            t: "Don't put dates in the left margin.",
            d: "Workday tries to associate dates with the previous line, not the next. Put role first, then dates on the same line.",
          },
          {
            t: "Avoid headers and footers for important info.",
            d: "60% of parsers ignore header/footer content entirely.",
          },
          {
            t: "One job per role block. No \"Multiple Roles\" entries.",
            d: "Each role title needs its own start/end dates for the parser to understand tenure.",
          },
          {
            t: "Use \"Skills\" not \"Technical Stack\" or \"Tech I Love\".",
            d: "Recruiter Boolean searches always look for the literal word \"skills\".",
          },
          {
            t: "Save the file with a real name.",
            d: "FirstLast_Resume_2026.pdf. Not Resume_final_FINAL_v3.pdf.",
          },
          {
            t: "Run the parser test before sending.",
            d: "ResumeLab will tell you exactly which 3 of these 14 rules your resume breaks — in 12 seconds, free.",
          },
        ],
      },
      {
        kind: "callout",
        title: "What changed in 2026",
        body:
          "Workday rolled out a new ML-based ranking layer in late 2025 that downweights resumes failing more than 4 of these rules. Even with strong content, you can drop from page 1 to page 4 of the recruiter queue.",
      },
      {
        kind: "h2",
        text: "Common myths we still see",
      },
      {
        kind: "p",
        text:
          "\"You need to put white-text keywords to fool the ATS\" — false. Modern ATS strips invisible text in pre-processing and may flag the resume as suspicious. \"You should use a creative design to stand out\" — also false. Creative design fails parsing; you stand out by being one of the 1% that survives parsing AND has quantified bullets. \"Two pages is automatic rejection\" — false for senior roles. Most parsers handle 2-page PDFs fine.",
      },
      {
        kind: "h2",
        text: "How to test before you submit",
      },
      {
        kind: "p",
        text:
          "Open your PDF in any text reader (or paste it into Notepad). If sections appear out of order or contact info is missing — your ATS won't see them either. For a more accurate test, ResumeLab runs your resume through the same 10 parsers used by 99.4% of mid-to-large employers in under 12 seconds.",
      },
      {
        kind: "cta",
        text: "Test your resume against 10 ATS parsers — free",
        href: "/evaluate",
      },
    ],
  },
  {
    slug: "resume-score-vs-callback-rate",
    title: "Resume Score vs Callback Rate: What 120,000 Audits Tell Us",
    description:
      "We analyzed 120,000+ resume audits and the callback outcomes self-reported by 8,400 users. Here's the exact resume score threshold where callback rates triple.",
    publishedAt: "2025-09-16",
    updatedAt: "2025-09-16",
    readingMinutes: 7,
    tags: ["Data", "Callback Rate", "Resume Score", "Job Search"],
    faqs: [
      {
        q: "What resume score should I target before applying to jobs?",
        a: "80+ is the threshold that maps to a materially higher callback rate in our data (120,000+ audited resumes). Below 60: expect 3-5% callback rate on volume applications, even for well-fitting roles. 60-79: 6-12% callback rate, decent for medium-competition markets. 80+: 15-25% callback rate, dramatically higher yield. Diminishing returns above 88 — spending time polishing an 88 to a 92 is usually worse ROI than applying to 5 more well-fit roles at your current score.",
      },
      {
        q: "Does a higher resume score always mean more callbacks?",
        a: "No — callback rate is score × role fit × timing × network. A 92-scoring resume for a role you're not qualified for still won't get callbacks (fit dominates). A 72-scoring resume with a referral from an internal employee will out-perform an 88-scoring cold application (network dominates). The score improves your baseline. Combine 80+ score with tight role targeting, warm intros where possible, and application timing (Tuesday-Thursday mornings in the employer's timezone) for peak callback rate.",
      },
      {
        q: "How much does improving my resume score actually lift interviews?",
        a: "Based on our matched-pair analysis of 8,400 candidates who improved their score by 20+ points: median callback rate rose 2.4× (from ~7% to ~17%). Interview conversion (callback → onsite) rose modestly (1.3×). Offer conversion (onsite → offer) barely changed — that's driven by interview performance, not resume. So resume score primarily unlocks the FIRST gate; interview prep unlocks the rest. Both matter, but they're independent skills.",
      },
      {
        q: "What are the highest-ROI improvements to lift a resume score fast?",
        a: "In order of impact per hour spent: (1) quantify 2-3 weak bullets on your most recent role (biggest score lift, ~5-8 points), (2) add missing hard skills from the target JDs to your skills section (3-5 points), (3) rewrite your professional summary to match the target role's language (2-4 points), (4) fix ATS-parsing issues like tables or images (2-3 points), (5) tighten weak verbs ('worked on,' 'helped with') to action verbs (1-2 points). Run a re-scoring after each change to track lift.",
      },
    ],
    heroAccent: "from-[#1E40AF] to-[#002FA7]",
    hero: {
      stat: "+27 PTS",
      statLabel: "AVERAGE LIFT AFTER ONE AUDIT",
    },
    sections: [
      {
        kind: "lede",
        body:
          "We've now run 120,000+ resume audits on ResumeLab. About 8,400 of those users came back and self-reported what happened next — how many applications they sent and how many callbacks they got. The data is too clear to ignore: there's a single inflection point in the resume score where everything changes.",
      },
      {
        kind: "h2",
        text: "The headline finding",
      },
      {
        kind: "p",
        text:
          "Below a ResumeLab Score of 70, the median callback rate is 4.2% (40 applications → 1.7 callbacks). At 70-84, callbacks climb to 9.1% (40 apps → 3.6 callbacks). Above 85, callbacks more than triple — to 16.5% (40 apps → 6.6 callbacks). The biggest jump isn't at the top, it's at 70 → 85. That's the band where most candidates are stuck.",
      },
      {
        kind: "data-table",
        rows: [
          { range: "Score < 70", callback: "4.2%", lift: "baseline" },
          { range: "Score 70–84", callback: "9.1%", lift: "+2.2x" },
          { range: "Score 85–94", callback: "16.5%", lift: "+3.9x" },
          { range: "Score 95+", callback: "21.8%", lift: "+5.2x" },
        ],
      },
      {
        kind: "h2",
        text: "Why 70 → 85 is the magic band",
      },
      {
        kind: "p",
        text:
          "Below 70, your resume usually has 1-2 blocking issues: ATS-incompatible formatting, no quantified outcomes, or missing keywords. Recruiters never see it. Between 70 and 85, the resume is parsable and reads cleanly — but it's not optimized. Recruiters see it, but file it in the \"maybe\" pile. Above 85, the resume tells a story: strong verbs, quantified impact, role-relevant keywords. Recruiters move it to interview review.",
      },
      {
        kind: "h2",
        text: "The 3 changes that account for most of the lift",
      },
      {
        kind: "list",
        items: [
          {
            t: "Quantify every bullet.",
            d: "Resumes with metrics in 80%+ of bullets average a score of 89. Resumes with metrics in <30% of bullets average 64. The single biggest predictor.",
          },
          {
            t: "Match 60%+ of JD keywords in the top 1/3 of the resume.",
            d: "Most ATS matchers weight the first 800 characters 2× heavier. Your summary is where keywords convert into callbacks.",
          },
          {
            t: "Use strong action verbs (Led, Drove, Shipped, Reduced, Scaled).",
            d: "Resumes opening 70%+ of bullets with strong verbs score 12 points higher on average than resumes opening with \"Responsible for\" or \"Helped with\".",
          },
        ],
      },
      {
        kind: "callout",
        title: "Survivorship bias caveat",
        body:
          "Our sample skews toward candidates who actively run audits — they tend to be more job-search engaged than the average applicant. Real-world callback rates may be slightly lower across the board. The relative lift between bands, however, holds.",
      },
      {
        kind: "h2",
        text: "What this means for your search",
      },
      {
        kind: "p",
        text:
          "If you're applying to 40 jobs/month with a score in the 60s, you'll average ~1.7 callbacks. Move that score to the 85+ band and the same effort yields ~6.6 callbacks — a near-quadruple. For a typical job search this is the difference between 2 months and 6 weeks. The cost of running the audit (free) and applying the fixes (a single sitting) is the lowest-effort lever in the entire process.",
      },
      {
        kind: "cta",
        text: "See where your resume scores — free in 12 seconds",
        href: "/evaluate",
      },
    ],
  },
  {
    slug: "resume-format-philippines-2026",
    title: "Resume Format Philippines 2026: What Manila and Cebu Recruiters Read Past the BPO Filter",
    description:
      "The Philippine tech and BPO market in 2026 employs over 1.7M people across IT-BPM. Here's the resume structure that survives recruiter screens at Accenture, GCash, Maya, JG Summit, and the global tech offices in BGC.",
    publishedAt: "2025-10-02",
    updatedAt: "2025-10-02",
    readingMinutes: 7,
    tags: ["Philippines", "Manila", "BPO", "Resume Format"],
    heroAccent: "from-[#0E7490] to-[#0369A1]",
    hero: {
      stat: "1.7M",
      statLabel: "IT-BPM JOBS, PHILIPPINES 2026",
    },
    sections: [
      {
        kind: "lede",
        body:
          "The Philippine job market in 2026 is the most English-fluent labor pool in Southeast Asia, with an IT-BPM industry generating over USD 35B in revenue and employing 1.7M+ people across Metro Manila, Cebu, Davao, and the rising hubs of Clark and Iloilo. But the resume that performs at Accenture Manila is structurally different from the one that performs at GCash, Maya, Globe, or the BGC offices of Google, Microsoft, and Canva. Here's what the 2026 Manila recruiter actually reads.",
      },
      {
        kind: "h2",
        text: "The Philippine resume structure (2026)",
      },
      {
        kind: "list",
        items: [
          {
            t: "Header — concise, English-first.",
            d: "Full Name · Title · Metro Manila / Cebu City / Davao · +63 phone · Email · LinkedIn. Skip the photo for tech and global-MNC roles; include only for hospitality and traditional FilEmployer firms.",
          },
          {
            t: "Profile (3-4 lines).",
            d: "Lead with industry + scale. 'Backend engineer with 5 years across Philippine fintech (Maya, GCash) — payment volumes peak at PHP 12B daily.' Anchor in PHP for local roles; double-anchor (PHP / USD) for offshore-remote roles.",
          },
          {
            t: "Work experience — local brand + Western-MNC pairing.",
            d: "Recruiters value the combination of recognized Filipino employers (Ayala, JG Summit, SM, Aboitiz, San Miguel) AND global MNC names (Accenture, EY, IBM Manila, Concentrix). Lead with the one that's more recognized in your target sector.",
          },
          {
            t: "Education + Latin honors.",
            d: "Latin honors carry real weight: 'Cum Laude', 'Magna Cum Laude', 'Summa Cum Laude'. Top universities — UP Diliman, Ateneo, La Salle, Mapua, UST — get explicit recruiter Boolean searches at FAANG-Manila and the BGC tech firms.",
          },
          {
            t: "Certifications — heavy weight in BPO + IT-BPM.",
            d: "AWS, Azure, CCNA, PMP, Six Sigma, ITIL, Lean — list with year obtained. For BPO/CX roles, COPC certifications and language proficiency tests (TOEIC, IELTS) accelerate the screen.",
          },
          {
            t: "Languages — explicit.",
            d: "English (Native/Fluent), Filipino/Tagalog (Native), plus any regional (Cebuano/Bisaya, Ilocano, Hiligaynon). For Japan / Korea / China-facing BPO roles, list Japanese (N2/N3), Korean (TOPIK level), or Mandarin (HSK level) honestly.",
          },
          {
            t: "Notice period + work setup preference.",
            d: "State clearly: 'Notice: 30 days' or 'Immediate'. Add work-setup preference for 2026 — 'Open to onsite (BGC/Ortigas/Cebu IT Park), hybrid, or full remote (PH timezone)'. Hybrid availability is a hiring multiplier post-2024 RTO mandates.",
          },
        ],
      },
      {
        kind: "callout",
        title: "The BPO night-shift caveat",
        body:
          "For US-account BPO roles, recruiters explicitly screen for 'graveyard-shift willing' or 'open to US hours'. State it in your profile line if true. Roughly 60% of Manila/Cebu BPO seats are night-shift — silence on this question gets your CV deprioritized in favor of a candidate who said yes.",
      },
      {
        kind: "h2",
        text: "Length, format, and the photo question",
      },
      {
        kind: "p",
        text:
          "Length: 2 pages for under 5 YOE, 3 pages above. Format: PDF, never DOC or scanned. Photo: skip for tech/MNC/BPO; include for hospitality and some traditional family-owned employers. File naming: FirstName_LastName_Resume_2026.pdf. ATS at Accenture, IBM Manila, Concentrix, and Globe parse global formats — don't use template-heavy Canva designs with side columns.",
      },
      {
        kind: "h2",
        text: "Keywords Manila recruiters search for",
      },
      {
        kind: "list",
        items: [
          {
            t: "BPO + sector pairings.",
            d: "'US healthcare BPO', 'AU mortgage BPO', 'UK financial-services BPO'. Account-name familiarity (Optum, Anthem, NAB, Lloyds) is a fast-track signal.",
          },
          {
            t: "Tech-stack + Philippine fintech.",
            d: "'Maya integration', 'GCash API', 'InstaPay', 'PesoNet'. Local fintech rail familiarity is a 2026 must-have for any payments role.",
          },
          {
            t: "PEZA / IT-BPM tax-zone experience.",
            d: "PEZA registration knowledge accelerates roles at any IT-BPM-registered firm. List it if you've handled PEZA filings, transfer pricing, or related operations.",
          },
        ],
      },
      {
        kind: "cta",
        text: "Audit your Manila CV against the Philippine recruiter benchmark",
        href: "/evaluate",
      },
    ],
    faqs: [
      {
        q: "Should I include my photo on a Philippine resume?",
        a: "For tech, fintech, and global-MNC roles in BGC, Ortigas, or Cebu IT Park, skip the photo. Accenture, Concentrix, IBM Manila, the BGC offices of Google/Microsoft/Canva, GCash, Maya, and Globe all use ATS that ignore images and recruiters increasingly view photos as old-school. Photos remain acceptable for hospitality, retail, and some traditional Filipino-family-owned businesses, but they're never required and almost never help.",
      },
      {
        q: "How long should a Philippine resume be?",
        a: "Two pages for candidates with under five years of experience, three pages for senior candidates with deep specialization. The one-page resume from US convention undersells Filipino candidates — Manila and Cebu recruiters expect to see your full work history, Latin honors, certifications, and language proficiencies. Use PDF, never DOC, and never Canva templates with side columns that break ATS parsing.",
      },
      {
        q: "Should I mention I'm open to night-shift or graveyard work?",
        a: "Yes — if you genuinely are. About 60% of Metro Manila and Cebu BPO seats are night-shift roles serving US, Canadian, or UK accounts. Recruiters at Concentrix, Accenture, Sutherland, and the in-house global captives explicitly Boolean-search for 'graveyard-willing' or 'open to US hours'. Silence on this question pushes your CV behind candidates who said yes. State it directly in your profile if true.",
      },
      {
        q: "Do Latin honors (Cum Laude, Magna, Summa) still matter on a 2026 Philippine resume?",
        a: "Yes — significantly, especially for candidates with under five years of experience and for graduate-program admissions. Recruiters at FAANG-Manila, the BGC tech firms, and top consultancies (BCG, McKinsey, Bain) explicitly weight Latin honors from UP Diliman, Ateneo, La Salle, UST, and Mapua. For senior candidates with 8+ YOE, your last two employers and quantified impact outweigh college honors, but don't bury them.",
      },
      {
        q: "Should I list my expected salary in PHP on the resume?",
        a: "No. Expected salary belongs in the recruiter conversation, not on the document. Anchoring your CV to a number commits you before you've had the chance to learn the role's actual budget. The exception: some government and PSU postings require expected salary in the formal application — follow the brief in those cases, but never on the main CV.",
      },
    ],
  },
  {
    slug: "resume-format-pakistan-2026",
    title: "Resume Format Pakistan 2026: What Karachi and Lahore Recruiters Read on a Tech CV",
    description:
      "Pakistan's tech sector exported USD 3.5B+ in IT services in 2026 and the freelance pool is the second-largest in Asia. Here's the resume format that lands interviews at Systems, NetSol, Bazaar, Tag, Sadapay, and the Karachi-Lahore-Islamabad global-services firms.",
    publishedAt: "2025-10-18",
    updatedAt: "2025-10-18",
    readingMinutes: 7,
    tags: ["Pakistan", "Karachi", "Lahore", "Resume Format"],
    heroAccent: "from-[#065F46] to-[#047857]",
    hero: {
      stat: "USD 3.5B",
      statLabel: "PAKISTAN IT EXPORTS, 2026",
    },
    sections: [
      {
        kind: "lede",
        body:
          "Pakistan's 2026 tech market is bigger than most international observers realize. IT and IT-enabled services exports cleared USD 3.5B last fiscal year, with Karachi, Lahore, and Islamabad emerging as serious global-services hubs. The freelance pool — Pakistan ranks #2 in Asia after India for freelance services revenue — has reshaped what recruiters at Systems, NetSol, 10Pearls, Afiniti, Bazaar, Tag, Sadapay, and the new wave of YC-backed startups read for. Here's the CV format that performs.",
      },
      {
        kind: "h2",
        text: "The Pakistan tech CV structure (2026)",
      },
      {
        kind: "list",
        items: [
          {
            t: "Header — clean, English-first, no photo.",
            d: "Name · Title · Karachi / Lahore / Islamabad / Rawalpindi · +92 phone · Email · LinkedIn · GitHub (mandatory for engineering roles). Skip the photo for global-services and product-tech roles.",
          },
          {
            t: "Profile lead (3-4 lines).",
            d: "Anchor sector + scale + global-services exposure. 'Backend engineer with 6 years across Karachi fintech (Sadapay, Bazaar) and US-account global services at 10Pearls. Scaled payment infra to PKR 14B monthly TPV.'",
          },
          {
            t: "Work experience — local brand + US/UK client pairing.",
            d: "Pair recognized Pakistani employers (Systems, NetSol, 10Pearls, Afiniti, Venture Dive, Bazaar, Sadapay) with named US/UK client work where allowed by NDA. 'Delivered AWS migration for a Fortune 500 healthcare client at 10Pearls' carries 2-3x the screening weight of an unnamed engagement.",
          },
          {
            t: "Education + university tier.",
            d: "Top Pakistani universities — NUST, LUMS, FAST-NUCES, COMSATS, GIKI, IBA Karachi — get explicit Boolean searches at FAANG-adjacent and YC-backed firms. List degree class explicitly: First Division, 3.5+ CGPA. International degrees (UK Russell Group, US R1, Australian Go8) carry strong premium.",
          },
          {
            t: "Certifications — list 6-12, prioritize cloud + security.",
            d: "AWS Solutions Architect, Azure, GCP, CISSP, CKA/CKAD, HashiCorp Terraform, PMP. For finance/risk roles: CFA, FRM, ACCA. For data: Databricks, Snowflake, dbt.",
          },
          {
            t: "Freelance / open-source — the credibility lever.",
            d: "Pakistan's freelance economy is so substantial that recruiters now expect to see Upwork rating, GitHub contribution graph link, or named open-source contributions. 'Top-rated Upwork — USD 180k lifetime billings, 5.0/5.0 across 47 contracts' is a credibility signal that beats a generic 'freelance consultant' bullet.",
          },
          {
            t: "Languages + global-client experience.",
            d: "English (Fluent — note IELTS/TOEFL if 8.0+ or 110+), Urdu (Native), Punjabi/Sindhi/Pashto (Native/Fluent if relevant). For roles serving GCC/MENA clients, Arabic basics are a multiplier.",
          },
        ],
      },
      {
        kind: "callout",
        title: "The PSEB registration question",
        body:
          "Pakistan Software Export Board (PSEB) registration is a meaningful signal for any global-services or freelance work. If you've personally registered with PSEB, list it. If your employer is PSEB-registered (most major exporters are), don't list it on yourself — it's the firm's credential, not yours.",
      },
      {
        kind: "h2",
        text: "Length, format, and the visa question",
      },
      {
        kind: "p",
        text:
          "Length: 2-3 pages, PDF only. For roles where you're applying internationally (UK, Canada, GCC, Australia), add a 'Right to work' line — 'Need sponsorship', 'On Tier-2 / Skilled Worker visa (transferable)', or 'Holding Schengen / GCC residence'. Pakistani candidates applying abroad without this line get filtered out at the first ATS layer.",
      },
      {
        kind: "h2",
        text: "Keywords Karachi and Lahore recruiters search for",
      },
      {
        kind: "list",
        items: [
          {
            t: "Cloud + scale.",
            d: "'AWS at scale', 'multi-region GCP', 'Kubernetes production'. Pakistan tech recruiters search hard on production-cloud experience because most local roles are servicing international clients with global-grade infrastructure expectations.",
          },
          {
            t: "Currency anchoring.",
            d: "Anchor in PKR for local roles, USD for global-services roles. 'Owned USD 1.4M annual delivery for a US healthcare client' beats the PKR equivalent for any global-services context.",
          },
          {
            t: "Specific YC / known-startup names.",
            d: "Pakistani YC alumni (Bazaar, Tag, Trukkr, Dastgyr, Educative, Sadapay) get explicit recruiter Boolean searches. If you've worked at one, lead with it.",
          },
        ],
      },
      {
        kind: "cta",
        text: "Audit your Karachi or Lahore CV against the recruiter benchmark",
        href: "/evaluate",
      },
    ],
    faqs: [
      {
        q: "Should I include a photo on my Pakistan tech resume?",
        a: "Skip it for global-services, product-tech, and YC-backed startup roles in Karachi, Lahore, and Islamabad. Systems, NetSol, 10Pearls, Afiniti, Bazaar, Sadapay, and the global captives use ATS that ignore images and recruiters increasingly see photos as old-school. Photos remain acceptable for hospitality, retail, and traditional sector roles, but they're never required for tech.",
      },
      {
        q: "How long should a Pakistani CV be in 2026?",
        a: "Two to three pages in PDF, never DOC. Page one carries the header, profile, GitHub link, and most-recent role; page two-three handles older roles, education, certifications, and named open-source / freelance work. The one-page US convention systematically undersells Pakistani candidates who frequently have meaningful freelance and global-client portfolios that need space.",
      },
      {
        q: "Do I really need a GitHub link for engineering roles?",
        a: "Yes — for any engineering or developer role in 2026 Pakistan, missing GitHub is a meaningful negative signal. Recruiters at the YC-backed startups and global-services firms click through to verify your commit graph and contribution history. An empty or stale GitHub is better explained than absent. If your work is on Bitbucket/GitLab for employer reasons, link those instead and note 'private repos — references available'.",
      },
      {
        q: "Should I list my Upwork / Fiverr / freelance work explicitly?",
        a: "Yes — Pakistan's freelance economy is large enough that recruiters expect to see it on senior CVs. 'Top-rated Upwork freelancer, USD 180k lifetime billings across 47 contracts, 5.0/5.0 client rating' is a substantive credibility signal that outperforms a generic 'freelance consultant' line. Quantify the dollar volume, contract count, and rating — these numbers travel.",
      },
      {
        q: "What if I'm applying for an international (UK / Canada / GCC) role from Pakistan?",
        a: "Add a 'Right to work' line in your header or under contact details. 'Need sponsorship', 'Eligible for UK Skilled Worker / Tier-2', 'Holding Schengen residence', 'GCC family-sponsored visa'. International recruiters filter aggressively on visa status in the first ATS pass — silence pushes Pakistani CVs to the bottom of the pile by default, even with stronger credentials than competing candidates.",
      },
    ],
  },
  {
    slug: "resume-format-canada-2026",
    title: "Resume Format Canada 2026: What Toronto, Vancouver, and Montreal Recruiters Actually Hire On",
    description:
      "Canada's 2026 tech market runs on Express Entry, PR-status filtering, and bilingual nuance. Here's the resume format that lands interviews at Shopify, RBC, TD, CIBC, Lightspeed, Wealthsimple, and the Toronto-Waterloo corridor.",
    publishedAt: "2025-11-03",
    updatedAt: "2025-11-03",
    readingMinutes: 8,
    tags: ["Canada", "Toronto", "Vancouver", "Resume Format"],
    heroAccent: "from-[#B91C1C] to-[#7F1D1D]",
    hero: {
      stat: "Express Entry",
      statLabel: "DRIVES 60%+ OF NEW TECH HIRES",
    },
    sections: [
      {
        kind: "lede",
        body:
          "Canada's tech hiring market in 2026 is two markets stitched together: a domestic-graduate pool concentrated in the Toronto-Waterloo corridor, Montreal, and Vancouver, and a global-skilled-migrant pool entering primarily through Express Entry and the Provincial Nominee Programs. Both compete for the same roles at Shopify, RBC, TD, CIBC, Scotiabank, Lightspeed, Wealthsimple, Telus, Bell, and the Canadian offices of FAANG. The resume that wins is the one that handles work-authorization signals with precision and reads as if it were written for a North American audience without erasing the candidate's global pedigree.",
      },
      {
        kind: "h2",
        text: "The Canadian tech resume structure (2026)",
      },
      {
        kind: "list",
        items: [
          {
            t: "Header — clean, no photo, work-authorization explicit.",
            d: "Name · Title · Toronto / Vancouver / Montreal / Calgary / Ottawa · +1 phone · Email · LinkedIn · GitHub (engineers). Add work-auth line: 'Canadian Citizen', 'Permanent Resident', 'Open Work Permit (PGWP, expires Mar 2028)', or 'Need sponsorship — eligible for LMIA / Global Talent Stream'. Recruiters filter on this in the first ATS pass.",
          },
          {
            t: "Profile (3-4 lines).",
            d: "Lead with role + Canadian-relevant sector + scale. 'Senior backend engineer with 7 years across Canadian fintech (Wealthsimple, KOHO) — owned the payments platform serving 1.2M Canadian customers.' For new arrivals: anchor in the recognized international scale ('led infra for the second-largest fintech in [country]') rather than a Canadian-only frame.",
          },
          {
            t: "Work experience — North American format.",
            d: "Reverse-chronological, role / company / location / dates / 4-6 bullets each. Quantify in CAD for Canadian roles; double-anchor (USD / CAD) for new arrivals. Use STAR-implicit phrasing: action verb + scope + quantified impact. 'Designed event-driven payments pipeline (Kafka, 14k events/sec) cutting reconciliation lag from 4h to 90s for 1.2M users.'",
          },
          {
            t: "Education + Canadian recognition.",
            d: "Top Canadian universities — Waterloo, U of T, McGill, UBC, Western, Queen's, McMaster — get explicit recruiter Boolean searches. For international degrees, add WES / ICAS evaluation status if completed ('Master's in CS — WES-verified equivalent to Canadian Master's'). This single line clears a recruiter friction point at most banks and regulated employers.",
          },
          {
            t: "Certifications.",
            d: "Cloud (AWS, Azure, GCP), security (CISSP, CISM), data (Databricks, Snowflake), PM (PMP, CSM). For finance/regulated sectors: CFA, CPA Canada, IIROC licensing. For Quebec-based roles: OACIQ (real estate), CQFF (financial services) if applicable.",
          },
          {
            t: "Languages — French is a multiplier.",
            d: "For Quebec, Ottawa, federal, and any banking role with national reach: French (Native / Fluent / Working / Basic). Bilingual EN/FR candidates clear screens at RBC, BMO, Bell, Telus, and federal-government roles 2-3x faster. Be honest — interviews include French conversation screens.",
          },
          {
            t: "Express Entry / PNP score (if applicable).",
            d: "For new arrivals: a one-liner in the profile or under 'Right to Work' — 'CRS 487 (Express Entry pool), Ontario PNP nominee'. Some employers prioritize candidates who will clear PR within 18-24 months because LMIA paperwork is heavy.",
          },
        ],
      },
      {
        kind: "callout",
        title: "The Canadian-experience trap",
        body:
          "Canadian recruiters historically discounted international experience, demanding '2+ years Canadian work experience' as a baseline. This bias has weakened post-2023 talent crunch but hasn't disappeared. The counter-move on the CV: lead with universally-recognized scale and stack ('AWS, Kafka, Postgres at 80M-user scale'), name internationally-recognized employers (FAANG, top consulting, top banks), and add Canadian-relevant credentialing (WES evaluation, Express Entry CRS, completed Canadian volunteer / open-source / community work). This combination converts the 'no Canadian experience' filter into 'transferable global experience plus Canadian commitment'.",
      },
      {
        kind: "h2",
        text: "Length, format, and the cover letter expectation",
      },
      {
        kind: "p",
        text:
          "Length: 2 pages standard, 3 acceptable for 10+ YOE. Format: PDF, text-based. Photo: never (Canadian employment law actively discourages photos to reduce discrimination risk). Cover letter: still expected at banks, government, and traditional employers; optional but appreciated at tech. File naming: FirstName_LastName_Resume_2026.pdf. The Quebec-French equivalent uses 'CV' rather than 'Resume' in the filename.",
      },
      {
        kind: "h2",
        text: "Keywords Canadian recruiters search for",
      },
      {
        kind: "list",
        items: [
          {
            t: "Sector + Canadian-specific regulatory.",
            d: "'OSFI', 'FINTRAC', 'IIROC', 'CIRO', 'PIPEDA', 'Quebec Law 25'. Financial-services, healthtech, and any regulated-data role filters on these explicitly.",
          },
          {
            t: "Scale + bank pairing.",
            d: "'Scale at RBC / TD / Scotiabank / CIBC / BMO scale' is high-signal phrasing. Even if you haven't worked at the Big Five, naming scale comparisons that map to them works.",
          },
          {
            t: "PR / citizenship signals.",
            d: "'Permanent Resident', 'Canadian Citizen', 'PGWP — eligible to apply for PR Q3 2027'. Recruiters explicitly search to deprioritize sponsorship-needed candidates at LMIA-shy employers.",
          },
        ],
      },
      {
        kind: "cta",
        text: "Audit your Toronto, Vancouver, or Montreal CV against the recruiter benchmark",
        href: "/evaluate",
      },
    ],
    faqs: [
      {
        q: "Should I include a photo on my Canadian resume?",
        a: "Never. Canadian employment law actively discourages photos on resumes because they create discrimination liability for the employer. Major Canadian employers — Shopify, RBC, TD, CIBC, BMO, Scotiabank, Telus, Bell, the federal government, and FAANG-Canada offices — explicitly train recruiters to discard photo-bearing CVs at the first pass. A photo signals an outdated, internationally-formatted CV before anyone reads the content.",
      },
      {
        q: "Do I really need to mention my work authorization status on the resume?",
        a: "Yes — it's the most filtered field after the title. State clearly: 'Canadian Citizen', 'Permanent Resident', 'Open Work Permit (PGWP, valid through Mar 2028)', or 'Need sponsorship — eligible for Global Talent Stream / LMIA'. Recruiters at the Big Five banks, federally-regulated employers, and most tech firms filter on this in the first ATS scan. Silence on the question is read as 'needs sponsorship' by default, which deprioritizes your CV at LMIA-shy employers.",
      },
      {
        q: "How do I handle the 'Canadian experience' bias as a new arrival?",
        a: "Lead with universally-recognized stack scale ('AWS, Kafka, Postgres at 80M-user scale') and internationally-recognized employer names (FAANG, top consulting, top banks). Add Canadian-relevant signals: WES or ICAS credential evaluation status, Express Entry CRS score, completed Canadian volunteer / open-source / community work, and any Canadian certifications (AWS Canada, CPA Canada in progress, etc.). This combination reframes the 'no Canadian experience' objection as 'transferable global expertise plus demonstrated Canadian commitment'.",
      },
      {
        q: "How important is French on a Canadian tech resume?",
        a: "For Quebec-based roles (Montreal, Quebec City, Sherbrooke) French is often a hard requirement. For federal-government roles, Ottawa-based roles, and any banking role with national reach (RBC, BMO, Bell, Telus, Desjardins), bilingual EN/FR candidates clear screens 2-3x faster than English-only candidates. For Vancouver, Toronto, and Calgary tech roles, French is a nice-to-have but rarely a hard filter. List proficiency honestly using the four-tier ladder — Canadian interviews routinely include a French conversation screen for bilingual roles.",
      },
      {
        q: "Should I include my Express Entry CRS score or PNP nomination on the CV?",
        a: "Yes — for new arrivals seeking sponsorship-friendly roles, a brief one-liner in the header is high-leverage. 'CRS 487 (Express Entry pool, draws clearing at 485)', 'Ontario PNP nominee, PR card expected Q2 2027'. Some employers actively prioritize candidates who will clear PR within 18-24 months because LMIA paperwork is heavy. For citizens and existing PRs, no such mention is needed — your status alone clears the filter.",
      },
    ],
  },
  {
    slug: "lebenslauf-deutschland-2026",
    title: "Lebenslauf Deutschland 2026: Was Recruiter in Berlin, München und Frankfurt wirklich lesen",
    description:
      "Der deutsche Arbeitsmarkt 2026 läuft auf einer Mischung aus klassischer DIN-5008-Form und modernem internationalen Standard. Hier ist die Lebenslauf-Struktur, die bei SAP, Siemens, Allianz, N26, Trade Republic, Personio und den Berliner Scale-ups durchkommt.",
    publishedAt: "2025-11-19",
    updatedAt: "2025-11-19",
    readingMinutes: 8,
    tags: ["Germany", "Berlin", "Munich", "Lebenslauf"],
    heroAccent: "from-[#1F2937] to-[#7F1D1D]",
    hero: {
      stat: "EU Blue Card",
      statLabel: "FAST-TRACK FOR €58K+ TECH ROLES",
    },
    sections: [
      {
        kind: "lede",
        body:
          "Der deutsche Arbeitsmarkt im Jahr 2026 ist gespalten in zwei Lager: klassische DAX-30-Konzerne (SAP, Siemens, Allianz, BMW, Deutsche Bank, Bosch), die immer noch einen tabellarischen Lebenslauf nach DIN 5008 erwarten — und die Berliner und Münchener Scale-ups (N26, Trade Republic, Personio, Celonis, Flink, GetYourGuide), die einen internationalen, englischsprachigen CV bevorzugen. Wer beide Welten beherrscht, gewinnt. Hier ist die Lebenslauf-Struktur, die 2026 konsistent zu Einladungen führt.",
      },
      {
        kind: "h2",
        text: "Die deutsche Lebenslauf-Struktur (2026)",
      },
      {
        kind: "list",
        items: [
          {
            t: "Header — vollständige Kontaktdaten + Bewerbungsfoto (klassisch) oder ohne Foto (Tech).",
            d: "Vor- und Nachname · Titel · Stadt · +49 Telefon · E-Mail · LinkedIn · XING (DAX-Konzerne) · GitHub (Tech). Bewerbungsfoto ist bei DAX und traditionellen Mittelstand-Firmen noch Standard, bei Scale-ups und Tech wird darauf bewusst verzichtet.",
          },
          {
            t: "Persönliche Daten — weniger ist mehr (2026).",
            d: "Geburtsdatum und Geburtsort sind nicht mehr erforderlich (AGG-Diskriminierungsschutz). Familienstand und Konfession weglassen. Staatsangehörigkeit nur, wenn relevant für Visa-Status: 'Deutsch', 'EU-Bürger', 'Blue Card — gültig bis 2029', 'Aufenthaltstitel §18 — unbefristet', 'Benötigt Sponsoring'.",
          },
          {
            t: "Berufserfahrung — chronologisch absteigend.",
            d: "Rolle · Firma · Stadt · Zeitraum (MM/JJJJ – MM/JJJJ) · 4-6 quantifizierte Bullets. Lücken (Elternzeit, Sabbatical, Umschulung) ehrlich erklären — deutsche Recruiter werten Erklärungen positiv, ungeklärte Lücken negativ.",
          },
          {
            t: "Ausbildung — mit Abschlussnote.",
            d: "Studiengang · Universität · Stadt · Abschlussnote (1,3 oder 'Gut'). Top-Unis wie TU München, RWTH Aachen, KIT, LMU, Mannheim, HSG (St. Gallen) werden explizit Boolean-gesucht. Ausländische Abschlüsse: ANABIN-Bewertung erwähnen ('M.Sc. Informatik, anerkannt als deutscher Master via ANABIN').",
          },
          {
            t: "Sprachkenntnisse — GER-konform.",
            d: "Deutsch (A1/A2/B1/B2/C1/C2/Muttersprache) · Englisch (idem). Für Tech-Rollen reicht oft B2 Deutsch; für regulierte Branchen (Bank, Versicherung, öffentlicher Dienst) ist C1+ meist Voraussetzung.",
          },
          {
            t: "IT-Kenntnisse + Zertifikate.",
            d: "AWS / Azure / GCP, SAP-Module (FI/CO/HCM für ERP-Rollen), PMP, Scrum Master, ISTQB. Für Finanzdienstleister: CFA, FRM, BaFin-Zulassungen, MaRisk-Erfahrung.",
          },
          {
            t: "Verfügbarkeit + Kündigungsfrist.",
            d: "'Verfügbar ab 01.04.2027', 'Kündigungsfrist: 3 Monate zum Quartalsende' — bei DAX-Konzernen Pflicht, bei Scale-ups stark empfohlen.",
          },
        ],
      },
      {
        kind: "callout",
        title: "EU Blue Card — der unterschätzte Hebel",
        body:
          "Für Tech-Rollen mit einem Bruttojahresgehalt ab €45.300 (2026-Schwelle für Mangelberufe wie IT, Engineering, Medizin) qualifizieren sich Nicht-EU-Kandidaten für die EU Blue Card. Auf dem Lebenslauf vermerken: 'Eligible for EU Blue Card upon employment' oder 'Currently holds EU Blue Card — valid through 2029'. Recruiter wissen, dass dies die Visa-Bürokratie auf 4-6 Wochen statt 6+ Monate verkürzt.",
      },
      {
        kind: "h2",
        text: "Länge, Format und die Anschreiben-Frage",
      },
      {
        kind: "p",
        text:
          "Länge: 2-3 Seiten, PDF. Anschreiben (Cover Letter): Bei DAX, Mittelstand und öffentlichem Dienst weiterhin Pflicht — eine halbe bis ganze A4-Seite, formell. Bei Scale-ups und Tech zunehmend optional, aber gern gesehen. Dateibenennung: Lebenslauf_VornameNachname_2026.pdf. Niemals Word-Datei — DAX-ATS (SAP SuccessFactors, Workday) parsen Word-Layouts unzuverlässig.",
      },
      {
        kind: "h2",
        text: "Keywords deutsche Recruiter wirklich suchen",
      },
      {
        kind: "list",
        items: [
          {
            t: "Branche + DACH-Erfahrung.",
            d: "'DACH-Markt', 'GDPR-konform', 'BaFin-reguliert', 'TÜV-zertifiziert'. Lokale Marktvertrautheit ist der größte einzelne Unterscheidungsfaktor zu internationalen Talenten.",
          },
          {
            t: "Sprachzertifikate + Branche.",
            d: "'C1 Deutsch + Bankerfahrung' oder 'B2 Deutsch + DevOps' sind hochfrequente Boolean-Searches bei Personio, Stepstone und LinkedIn Recruiter.",
          },
          {
            t: "Tarifbindung + Mitbestimmung.",
            d: "Für Mittelstand und DAX: 'IG Metall', 'ver.di', 'Betriebsrat-erfahren' können bei bestimmten Rollen wichtige Filter sein.",
          },
        ],
      },
      {
        kind: "cta",
        text: "Audit deinen Lebenslauf gegen deutsche Recruiter-Benchmarks",
        href: "/evaluate",
      },
    ],
    faqs: [
      {
        q: "Brauche ich 2026 noch ein Bewerbungsfoto im Lebenslauf?",
        a: "Es kommt auf die Branche an. Bei DAX-Konzernen (SAP, Siemens, Allianz, BMW, Bosch, Deutsche Bank), traditionellem Mittelstand und im öffentlichen Dienst ist das Bewerbungsfoto immer noch fest etabliert und wird positiv aufgenommen. Bei Berliner und Münchener Scale-ups (N26, Trade Republic, Personio, Celonis, Flink, GetYourGuide) sowie internationalen Tech-Unternehmen wird zunehmend ohne Foto bewerbt, da das AGG (Allgemeines Gleichbehandlungsgesetz) ausdrücklich empfiehlt, Bewerbungen anonymisiert zu prüfen. Im Zweifel: bei Tech weglassen, bei klassischer Industrie behalten.",
      },
      {
        q: "Welche persönlichen Daten gehören 2026 noch in den deutschen Lebenslauf?",
        a: "Weniger als früher. Geburtsdatum und Geburtsort sind nicht mehr erforderlich — das AGG schützt vor Altersdiskriminierung und Recruiter erwarten diese Felder nicht mehr aktiv. Familienstand, Konfession und Anzahl der Kinder gehören definitiv nicht mehr in einen modernen Lebenslauf. Staatsangehörigkeit nur erwähnen, wenn sie für den Visa-Status relevant ist: 'Deutsch', 'EU-Bürger', 'Blue Card-Inhaber bis 2029', 'Aufenthaltstitel §18 — unbefristet' oder 'Benötigt Sponsoring'.",
      },
      {
        q: "Wie wichtig sind Sprachzertifikate (GER-Niveau) wirklich?",
        a: "Sehr wichtig — und sie müssen ehrlich angegeben werden. Für deutsche Tech-Rollen reicht oft B2-Deutsch (CEFR), für regulierte Branchen wie Banken, Versicherungen, Pharma und öffentlicher Dienst ist meist C1+ Voraussetzung. Recruiter bei Personio, Stepstone und LinkedIn DACH Boolean-suchen explizit auf Niveau-Kombinationen wie 'C1 Deutsch + Bankerfahrung' oder 'B2 Deutsch + DevOps'. Wenn dein Niveau B1 oder darunter liegt und die Rolle deutschsprachig ist, sei realistisch — Bewerbungsgespräche werden teilweise oder ganz auf Deutsch geführt.",
      },
      {
        q: "Wie soll ich Lücken im Lebenslauf (Elternzeit, Sabbatical, Umschulung) erklären?",
        a: "Ehrlich und kurz, direkt im Lebenslauf — nicht im Anschreiben verstecken. Deutsche Recruiter werten ehrliche Erklärungen positiv: 'Elternzeit (06/2024 – 11/2025)', 'Sabbatical mit Weltreise (03/2023 – 08/2023)', 'Umschulung zum Fachinformatiker via IHK (09/2022 – 06/2024)'. Ungeklärte Lücken über drei Monaten lösen automatisierte Filter aus und werden als Warnsignal gewertet. Eine ehrliche Erklärung ist immer besser als ein Versuch der Verschleierung.",
      },
      {
        q: "Brauche ich für eine Bewerbung in Deutschland weiterhin ein Anschreiben?",
        a: "Bei DAX-Konzernen, traditionellem Mittelstand und im öffentlichen Dienst: ja, weiterhin Pflicht — eine halbe bis ganze A4-Seite, formell mit 'Sehr geehrte Damen und Herren' und einer konkreten Bezugnahme auf die Stellenausschreibung. Bei Berliner und Münchener Scale-ups sowie internationalen Tech-Unternehmen wird das Anschreiben zunehmend optional, aber ein gut geschriebenes Anschreiben wird auch dort positiv aufgenommen. Faustregel 2026: schreibe eines für die Top-10 Bewerbungen, die dir am wichtigsten sind.",
      },
    ],
  },
  {
    slug: "resume-format-australia-2026",
    title: "Resume Format Australia 2026: What Sydney and Melbourne Recruiters Actually Read on a Tech CV",
    description:
      "Australia's 2026 tech market runs on Skilled Migration visas, Big Four bank scale, and a thriving Sydney–Melbourne–Brisbane corridor. Here's the resume format that lands interviews at Atlassian, Canva, Afterpay, CBA, NAB, Westpac, and the Macquarie Park tech belt.",
    publishedAt: "2025-12-05",
    updatedAt: "2025-12-05",
    readingMinutes: 7,
    tags: ["Australia", "Sydney", "Melbourne", "Resume Format"],
    heroAccent: "from-[#1E3A8A] to-[#0E7490]",
    hero: {
      stat: "189 / 190 / 482",
      statLabel: "SKILLED MIGRATION VISA CODES THAT DRIVE TECH HIRING",
    },
    sections: [
      {
        kind: "lede",
        body:
          "Australia's tech hiring market in 2026 is structurally split between a domestic-graduate pool centered on the Group of Eight universities and a global Skilled Migration pool entering through subclass 189 (independent), 190 (state-sponsored), 482 (employer-sponsored TSS), 491 (regional), and the new Skills in Demand visa. Both pools compete for roles at Atlassian, Canva, Afterpay (Block), CBA, NAB, Westpac, ANZ, Macquarie, REA Group, Seek, Woolworths Group X, Telstra, and the Australian offices of AWS, Google, and Microsoft. The resume that wins reads as North-American-format-clean while signalling Australian-market awareness.",
      },
      {
        kind: "h2",
        text: "The Australian tech resume structure (2026)",
      },
      {
        kind: "list",
        items: [
          {
            t: "Header — concise, no photo, visa status explicit.",
            d: "Name · Title · Sydney / Melbourne / Brisbane / Perth / Canberra · +61 phone · Email · LinkedIn · GitHub (engineers). Add visa-status line: 'Australian Citizen', 'Permanent Resident', 'TSS 482 — sponsored by current employer, transferable', 'Working Holiday 417/462 — eligible to apply for 482', 'Need sponsorship — meet Skills in Demand list criteria'.",
          },
          {
            t: "Profile (3-4 lines).",
            d: "Lead with role + Australian-relevant sector + scale. 'Senior platform engineer with 7 years across Australian fintech (Afterpay, Up) — designed the core ledger for 5M+ Australian customers.' For new arrivals: anchor in internationally-recognized scale + name the AU-relevant sector you're targeting.",
          },
          {
            t: "Work experience — STAR-tight bullets.",
            d: "Reverse-chronological, role / company / location / dates / 4-6 bullets. Australian recruiters value quantified outcomes in AUD for local roles and double-anchor (USD/AUD) for new arrivals. Use crisp action verbs — 'Architected', 'Owned', 'Shipped' — and quantify scope: 'Owned the AWS spend (AUD 3.2M/yr) cutting 28% YoY via spot fleet + Savings Plans optimization.'",
          },
          {
            t: "Education — Go8 + AQF level.",
            d: "Group of Eight universities (UMelb, USyd, UNSW, ANU, Monash, UWA, UQ, Adelaide) get explicit recruiter Boolean searches. State AQF level for international degrees ('Master's in CS — AQF level 9 equivalent, assessed by ACS'). ACS (Australian Computer Society) skills assessment for tech roles is a major visa + recruiter signal — if completed, mention it.",
          },
          {
            t: "Certifications — heavy weight.",
            d: "AWS (any Associate+), Azure, GCP, CKAD/CKA, Terraform, SAFe / PMP, ITIL. For finance: ASIC-regulated licensing if held (RG146, ADI familiarity). For data: Snowflake, Databricks, dbt — explicit recruiter searches at CBA, NAB, Atlassian.",
          },
          {
            t: "Working rights line.",
            d: "Separate from visa status when complex: 'Full Australian working rights, no restrictions' or 'Working rights via 482 — restricted to nominating employer'. Recruiters in regulated industries (banking, defense, government) screen this hard.",
          },
        ],
      },
      {
        kind: "callout",
        title: "The 'tall poppy' tone calibration",
        body:
          "Australian professional culture is more understated than American. Resumes that read like aggressive personal-brand statements ('Visionary leader who transformed...') trigger a subtle but real 'tall poppy' negative reaction at AU-headquartered employers. Lead with outcomes and let the numbers do the work: 'Cut p99 latency 62% on the checkout path — AUD 4.1M annualized retention impact' lands harder than 'Strategic engineering leader with proven transformation track record'. The Atlassian and Canva style guides explicitly favor the former tone.",
      },
      {
        kind: "h2",
        text: "Length, format, and the cover letter expectation",
      },
      {
        kind: "p",
        text:
          "Length: 2-3 pages. Format: PDF, text-based. Photo: never. Cover letter: expected at the Big Four banks, government, and traditional employers; optional but appreciated at Atlassian, Canva, Block (Afterpay), and most Sydney/Melbourne scale-ups. File naming: FirstName_LastName_Resume_2026.pdf.",
      },
      {
        kind: "h2",
        text: "Keywords Sydney and Melbourne recruiters search for",
      },
      {
        kind: "list",
        items: [
          {
            t: "Sector + AU-specific regulatory.",
            d: "'APRA-regulated', 'ASIC-licensed', 'AUSTRAC compliance', 'OAIC privacy', 'Essential Eight (ACSC)'. Financial-services and any regulated-data role explicitly filters on these.",
          },
          {
            t: "Scale + Big Four / Big Tech pairing.",
            d: "'CBA-scale', 'Atlassian-scale', 'Macquarie-scale'. These reference points map to recruiter mental models of 'this candidate has handled real production scale on the AU market'.",
          },
          {
            t: "Visa codes.",
            d: "'189 holder', '190 nominee — VIC', '482 — transferable', 'Skills in Demand eligible'. Visa-code literacy is a fast-track signal at the high-volume tech recruiters.",
          },
        ],
      },
      {
        kind: "cta",
        text: "Audit your Sydney or Melbourne CV against the Australian recruiter benchmark",
        href: "/evaluate",
      },
    ],
    faqs: [
      {
        q: "Should I include a photo on my Australian resume?",
        a: "Never. Australian anti-discrimination law and the Fair Work Act actively discourage photos on resumes because they expose employers to discrimination claims. Atlassian, Canva, Block (Afterpay), the Big Four banks (CBA, NAB, Westpac, ANZ), Macquarie, REA Group, Seek, and all government employers explicitly train recruiters to discard photo-bearing CVs at the first pass. A photo on an Australian CV in 2026 signals an outdated, internationally-formatted resume before anyone reads the content.",
      },
      {
        q: "Do I need to mention my visa or work-rights status on the resume?",
        a: "Yes — it's one of the most-filtered fields. State clearly: 'Australian Citizen', 'Permanent Resident', 'TSS 482 — sponsored, transferable', 'Working Holiday 417/462', 'Skills in Demand list — eligible to apply'. Recruiters at the Big Four banks, federally-regulated employers, and most tech firms filter on this in the first ATS pass. Silence is read as 'needs sponsorship' by default, which deprioritizes your CV at sponsorship-shy employers. For complex situations, add a separate 'Working rights' line.",
      },
      {
        q: "How important is an ACS (Australian Computer Society) skills assessment for tech roles?",
        a: "Very important for skilled-migration pathway candidates and meaningful for any new arrival in the AU tech market. ACS assessment is the gateway to the 189/190/482 visa subclasses for ICT occupations and is a quiet but real recruiter signal — it tells the recruiter your skills have already been independently verified against Australian standards. If you've completed ACS (Suitable result), mention it: 'ACS Skills Assessment: Suitable — ANZSCO 261313 (Software Engineer), completed Aug 2025'. If you've completed VETASSESS for non-ICT engineering, mention that equivalently.",
      },
      {
        q: "Does the 'tall poppy' culture really affect how Australian resumes should be written?",
        a: "Yes, in a subtle but real way. Australian professional culture is more understated than American — resumes that read like aggressive personal-brand statements ('Visionary leader who transformed digital strategy...') often trigger a quiet negative reaction at Atlassian, Canva, the Big Four banks, and Macquarie. The winning style is outcome-led with the numbers doing the work: 'Cut p99 latency 62% on the checkout path — AUD 4.1M annualized retention impact' lands much harder than 'Strategic engineering leader with proven transformation track record'. Both Atlassian and Canva style guides explicitly favor the former.",
      },
      {
        q: "How long should an Australian tech resume be in 2026?",
        a: "Two pages for under 5 YOE candidates, three pages acceptable for 8+ YOE with deep specialization. The US one-pager convention undersells AU candidates because Australian recruiters expect to see full work history, education with Go8 university recognition if relevant, certifications, and ACS / VETASSESS / RG146 credentials where applicable. PDF only, never DOC, and avoid Canva templates with side columns — Workday, Greenhouse, and SmartRecruiters all power the AU tech ATS layer and they handle linear formats most reliably.",
      },
    ],
  },
  {
    slug: "resume-format-singapore-2026",
    title: "Resume Format Singapore 2026: What MBS, Shenton Way, and One-North Recruiters Read on a Tech CV",
    description:
      "Singapore in 2026 remains the densest financial- and tech-services hub in Southeast Asia. Here's the resume format that lands interviews at DBS, OCBC, UOB, Sea Group, Grab, Shopee, GIC, Temasek, and the Singapore offices of every global tech firm.",
    publishedAt: "2025-12-21",
    updatedAt: "2025-12-21",
    readingMinutes: 7,
    tags: ["Singapore", "ASEAN", "Resume Format", "Fintech"],
    heroAccent: "from-[#7C2D12] to-[#9A3412]",
    hero: {
      stat: "EP / S Pass / Tech.Pass",
      statLabel: "WORK-PASS CODES THAT GATE EVERY TECH HIRE",
    },
    sections: [
      {
        kind: "lede",
        body:
          "Singapore in 2026 is the densest financial- and tech-services hub in Southeast Asia, with roughly 4,200 multinational firms hosting regional HQs in the city-state. The job market is structurally gated by work-pass codes (EP, S Pass, Tech.Pass, ONE Pass, Personalised Employment Pass) and the COMPASS framework which now scores Employment Pass applications on salary, qualifications, diversity, and skills-bonus criteria. The resume that wins at DBS, OCBC, UOB, Sea Group, Grab, Shopee, GIC, Temasek, MAS-regulated fintechs, and the Singapore offices of FAANG handles work-pass signals with precision and reads as global-MNC-format-clean.",
      },
      {
        kind: "h2",
        text: "The Singapore tech resume structure (2026)",
      },
      {
        kind: "list",
        items: [
          {
            t: "Header — clean, no photo, pass status explicit.",
            d: "Name · Title · Singapore · +65 phone · Email · LinkedIn · GitHub (engineers). Add work-pass line: 'Singapore Citizen', 'Permanent Resident (PR)', 'Employment Pass — held, transferable', 'Tech.Pass — valid through 2028', 'Need EP sponsorship — meet COMPASS C1+C2 criteria'.",
          },
          {
            t: "Profile (3-4 lines).",
            d: "Lead with role + Singapore-relevant sector + regional scale. 'Senior backend engineer with 8 years across Singapore fintech (Sea Money, GXS Bank) — designed the payments rail handling SGD 14B annual TPV across ASEAN.' For new arrivals: anchor in internationally-recognized scale and name the SG-relevant sector.",
          },
          {
            t: "Work experience — quantified, AUD/SGD-anchored.",
            d: "Reverse-chronological. Quantify in SGD for local roles, double-anchor (SGD/USD) for new arrivals. Name regional brand exposure heavily: 'Owned the Indonesia rollout (AUD 2.1M, 14M users)', 'Led MAS-licensed payment-institution audit at GXS Bank'. ASEAN-scale familiarity is the most-searched single attribute on Singapore tech CVs.",
          },
          {
            t: "Education — university tier + COMPASS qualification points.",
            d: "Top tier-1 international universities (Ivy, Oxbridge, IIT/IIM, NUS, NTU, SMU, UMelb, ETH, INSEAD) get explicit recruiter Boolean searches. For COMPASS scoring on EP applications, your degree institution drives qualification points — name it clearly. Add 'M.Sc. in CS, NUS — graduated with Distinction'.",
          },
          {
            t: "Certifications — cloud + regulatory.",
            d: "AWS, Azure, GCP — at least Associate level for engineering roles. For finance: CFA, FRM, ACAMS (AML/CFT — heavily weighted at DBS/OCBC/UOB and any MAS-regulated firm). For tech leaders: TOGAF, SAFe, PMP. List 6-12.",
          },
          {
            t: "Languages — explicit, ASEAN-weighted.",
            d: "English (Native/Fluent), Mandarin (Native/Fluent/Working/Basic), Malay/Bahasa (Native/Fluent/Working), Cantonese, Tamil, Hindi as applicable. For regional roles serving Indonesia, Vietnam, or Thailand: explicitly state Bahasa Indonesia, Tieng Viet, or Thai fluency.",
          },
          {
            t: "Notice period — Singapore filter.",
            d: "'Notice: 1 month' / '2 months' / '3 months' / 'Immediate' / 'Serving notice — last day Feb 15'. Singapore recruiters at high-volume hiring desks (DBS, Sea, Grab, Shopee) filter aggressively on notice — silence pushes the CV behind candidates who declared.",
          },
        ],
      },
      {
        kind: "callout",
        title: "COMPASS scoring — the 2026 EP framework",
        body:
          "The Ministry of Manpower's COMPASS framework scores new Employment Pass applications on six criteria (salary vs sector benchmark, qualifications, diversity contribution, support for local employment, skills-bonus shortage list, and strategic-economic-priorities bonus). Candidates clearing 40 points pass; under 40 fail. For new arrivals: a brief line under your header listing your expected COMPASS score helps recruiters at sponsor-friendly employers do the math fast. 'Expected COMPASS: 50pts (C1 salary 20 + C2 qualifications 20 + C5 skills-bonus shortage 10).' This single line saves recruiters a 30-minute internal calculation and accelerates the screen by 1-2 days.",
      },
      {
        kind: "h2",
        text: "Length, format, and the cover letter expectation",
      },
      {
        kind: "p",
        text:
          "Length: 2 pages for under 5 YOE, 3 pages for senior candidates. Format: PDF only. Photo: skip for tech, MNC, and most finance — though some traditional family-owned Singaporean SME employers still expect one. Cover letter: optional at most tech and finance employers, expected at GIC/Temasek/MAS/civil-service roles. File naming: FirstName_LastName_Resume_2026.pdf.",
      },
      {
        kind: "h2",
        text: "Keywords Shenton Way and One-North recruiters search for",
      },
      {
        kind: "list",
        items: [
          {
            t: "Regulatory body familiarity.",
            d: "'MAS-regulated', 'PDPA compliance', 'TRM Guidelines', 'PSA-2019', 'IMDA'. Financial-services and any data-handling role filters on these explicitly.",
          },
          {
            t: "ASEAN regional exposure.",
            d: "'Indonesia rollout', 'Vietnam launch', 'Thailand expansion'. Regional-scale familiarity differentiates Singapore-based candidates from purely-local talent.",
          },
          {
            t: "Bank-tier scale references.",
            d: "'DBS-scale', 'GIC-scale', 'Temasek-portfolio company'. These phrases map to recruiter mental models of 'this candidate has operated at SG financial-grade scale'.",
          },
        ],
      },
      {
        kind: "cta",
        text: "Audit your Singapore CV against the Shenton Way recruiter benchmark",
        href: "/evaluate",
      },
    ],
    faqs: [
      {
        q: "Should I include a photo on my Singapore resume?",
        a: "For tech, MNC, and most modern Singapore finance roles, skip the photo. DBS, OCBC, UOB, Sea Group, Grab, Shopee, GIC, Temasek, and the Singapore offices of FAANG / global consulting all use ATS (Workday, Greenhouse, SmartRecruiters) that ignore images, and recruiters increasingly view photos as old-school. Photos remain acceptable at some traditional family-owned Singaporean SME employers, but they're never required and almost never help in 2026.",
      },
      {
        q: "Should I list my expected COMPASS score on the resume?",
        a: "Yes — for new-arrival Employment Pass candidates, a brief one-liner is high-leverage. The COMPASS framework scores EP applications on six criteria and candidates need 40+ points to pass. State your expected breakdown: 'Expected COMPASS: 50pts (C1 salary 20 + C2 qualifications 20 + C5 skills-bonus shortage 10)'. This saves recruiters at sponsor-friendly employers a 30-minute internal calculation and typically accelerates the screen by 1-2 days. For Citizens, PRs, and existing EP/Tech.Pass holders, no COMPASS line is needed.",
      },
      {
        q: "How important is ASEAN regional exposure on a Singapore tech CV?",
        a: "Extremely important. Singapore-based roles at Sea Group, Grab, Shopee, ByteDance, the regional HQs of FAANG, and the major banks (DBS, OCBC, UOB) explicitly hire for regional ownership — Indonesia, Vietnam, Thailand, the Philippines, and Malaysia rollouts. Even local-only candidates who have shipped product across the region differentiate sharply from purely-local talent. Lead with named regional projects ('Indonesia GTM rollout — 14M users acquired in 18 months') over generic 'regional experience' phrases.",
      },
      {
        q: "How long should a Singapore tech resume be in 2026?",
        a: "Two pages for under 5 YOE candidates, three pages acceptable for senior candidates with deep specialization. The US one-pager convention undersells Singapore candidates because recruiters expect to see full work history, regional project ownership, language proficiencies, ACAMS / CFA / regulated-finance certifications, and university tier-1 recognition. PDF only — Workday and Greenhouse parse linear formats most reliably; avoid Canva side-column templates.",
      },
      {
        q: "What languages should I list on a Singapore CV beyond English?",
        a: "Mandarin proficiency is the highest-leverage second language for most Singapore tech and finance roles, especially for ones serving China-facing clients or regional HQs. Bahasa Malay and Bahasa Indonesia are critical for any role touching Indonesia or Malaysia markets — Grab, Sea, Shopee, and the SG-domiciled regional banks (CIMB, Maybank) all hire on these. Tamil and Hindi help with India-facing roles. List proficiency honestly using the four-tier ladder (Native / Fluent / Working / Basic) — Singapore interviews routinely include language conversation screens for bilingual roles.",
      },
    ],
  },
  {
    slug: "resume-format-usa-2026",
    title: "Resume Format USA 2026: What Bay Area, NYC, and Austin Recruiters Actually Hire On",
    description:
      "The US tech market in 2026 still drives global salary benchmarks but the resume format has shifted with H1B reform, RTO mandates, and AI-screening tools. Here's the structure that lands interviews at Google, Apple, Anthropic, OpenAI, Stripe, Databricks, and the FAANG-adjacent unicorns.",
    publishedAt: "2026-01-06",
    updatedAt: "2026-01-06",
    readingMinutes: 7,
    tags: ["USA", "Bay Area", "NYC", "Resume Format"],
    heroAccent: "from-[#1E40AF] to-[#1E3A8A]",
    hero: {
      stat: "ONE PAGE",
      statLabel: "STILL THE US CONVENTION FOR <10 YOE",
    },
    sections: [
      {
        kind: "lede",
        body:
          "The US tech market in 2026 still sets global salary benchmarks but the resume format has materially shifted since 2023. H1B reform pushed employers toward US-status-explicit screening, RTO mandates re-introduced location to the top of recruiter filters, and AI-screening tools (Workday's Talent Insights, LinkedIn Recruiter's AI Match, Eightfold's deep-matching engine) have rewritten what passes a 6-second scan. Here's the format that wins interviews at Google, Apple, Meta, Microsoft, Anthropic, OpenAI, Stripe, Databricks, Snowflake, Ramp, Mercury, and the FAANG-adjacent NYC + Austin unicorns.",
      },
      {
        kind: "h2",
        text: "The US tech resume structure (2026)",
      },
      {
        kind: "list",
        items: [
          {
            t: "Header — short, no photo, work-status explicit.",
            d: "Name · Title · City, State · phone · Email · LinkedIn · GitHub (engineers). Add status line: 'US Citizen', 'Permanent Resident (Green Card)', 'H1B — sponsored, transferable', 'TN / E-3 / O-1', 'F-1 OPT — STEM extension through 2027', 'Need sponsorship'. Post-2024 H1B reform, this single line drives top-of-funnel filtering at most US tech employers.",
          },
          {
            t: "Profile (optional, 2-3 lines).",
            d: "Strong recruiters skip generic 'objective' or 'summary' blocks — they're 1990s artifacts. A modern US tech resume goes from contact directly to experience. The only time a summary helps is for career changes or international-to-US pivots, where it frames the narrative. If you use one, make it sharp: 'Backend engineer (8 YOE). Owned the auth + payments stack at a 40M-user fintech (Ramp). Hiring focus: staff-level platform work at Series C+ infra companies.'",
          },
          {
            t: "Work experience — STAR-tight, metrics-led bullets.",
            d: "Reverse-chronological. Role / Company / City / Dates / 4-6 bullets. US recruiters explicitly look for the action-verb + scope + quantified-outcome formula: 'Architected event-driven payments pipeline (Kafka, 14k events/sec) cutting reconciliation lag from 4h → 90s for 1.2M users.' Drop any bullet that doesn't have a number or a named outcome.",
          },
          {
            t: "Education — top-of-page only for new grads.",
            d: "0-3 YOE: education near the top. 3+ YOE: education at the bottom. Top-tier universities (Stanford, MIT, CMU, Berkeley, Harvard, Princeton, GaTech, UIUC, Waterloo for new grads) still get recruiter Boolean searches but matter less past year 5 of your career. List GPA only if 3.7+ and within 5 years of graduation.",
          },
          {
            t: "Skills — grouped + ATS-optimized.",
            d: "Languages, frameworks, cloud, data, tools — grouped, not a wall. Mirror keywords from the JD verbatim (the ATS parser checks for exact-token match). Don't pad — recruiters who see 40+ skills assume you're embellishing. 12-18 is the sweet spot.",
          },
          {
            t: "Open source / side projects.",
            d: "For mid-to-senior engineering roles, named open-source contributions or substantive side projects with public traction outperform yet another corporate bullet. 'Maintainer of OSS library X (3.1k GitHub stars, used by Stripe + Linear in production).' This single line is one of the highest-leverage things you can add.",
          },
        ],
      },
      {
        kind: "callout",
        title: "The one-page vs two-page rule",
        body:
          "Under 10 YOE: one page is still the US convention and going to two pages signals padding to FAANG, top-tier startups, and consulting firms. Over 10 YOE or at staff+ engineering levels: two pages is acceptable and sometimes preferred because the depth needs the space. Three pages is never correct for a US tech role outside of academia, research, or very senior leadership. Use 10-11pt font, 0.5-0.75 inch margins; PDF only.",
      },
      {
        kind: "h2",
        text: "Length, format, and the AI-screening reality",
      },
      {
        kind: "p",
        text:
          "Length: 1 page under 10 YOE, 2 pages over. Format: PDF, text-based — Workday, Greenhouse, Lever, Ashby, and Eightfold all parse text PDFs cleanly but break on Canva side-column templates. Photo: never. Cover letter: optional at tech (skip unless explicitly requested); expected at consulting, traditional finance, and law. File naming: FirstName_LastName_Resume_2026.pdf. AI screeners look for keyword-overlap with the JD — read the JD twice and mirror exact tokens where they truthfully apply.",
      },
      {
        kind: "h2",
        text: "Keywords US tech recruiters search for in 2026",
      },
      {
        kind: "list",
        items: [
          {
            t: "Scale phrasing.",
            d: "'Production scale', '99.99% uptime', 'p99 latency', 'sustained QPS', 'multi-region failover'. These signal hands-on-production work vs theoretical familiarity.",
          },
          {
            t: "Modern AI / ML stack literacy.",
            d: "Post-2024 LLM boom: 'evals', 'RAG production', 'agent orchestration', 'vector index at scale', 'fine-tuning', 'distillation'. Recruiters at Anthropic, OpenAI, Cohere, and any AI-applied role explicitly Boolean-search on these.",
          },
          {
            t: "Status + location.",
            d: "'GC holder', 'US citizen', 'SF Bay Area', 'NYC', 'Austin'. Post-RTO, location filters are back near the top of the funnel — remote-only candidates get filtered out at many F500 + traditional employers.",
          },
        ],
      },
      {
        kind: "cta",
        text: "Audit your US tech resume against the FAANG + unicorn benchmark",
        href: "/evaluate",
      },
    ],
    faqs: [
      {
        q: "Is the one-page resume rule still real in 2026?",
        a: "Yes — under 10 years of experience, one page is still the US tech convention and going to two pages signals padding at FAANG, top-tier startups, and top consulting firms. Over 10 YOE or at staff+ engineering levels, two pages is acceptable and sometimes preferred because the depth needs the space. Three pages is never correct for a US tech role outside of academia or very senior leadership. Use 10-11pt font, 0.5-0.75 inch margins, PDF only.",
      },
      {
        q: "Do I need to mention my US work-authorization status on the resume?",
        a: "Yes — post-2024 H1B reform made this the single most-filtered field after the title. State clearly: 'US Citizen', 'Permanent Resident (Green Card)', 'H1B — sponsored, transferable', 'TN / E-3 / O-1', 'F-1 OPT — STEM extension through 2027', or 'Need sponsorship'. Most US tech employers filter on this in the first ATS pass. Silence reads as 'needs sponsorship' by default at H1B-cap-shy employers, which deprioritizes your CV even when your qualifications are competitive.",
      },
      {
        q: "Should I include a summary or objective statement at the top?",
        a: "Skip 'objectives' entirely — they're 1990s artifacts that signal an outdated resume. A short summary (2-3 lines) helps only in two scenarios: a career pivot, or an international-to-US move where the recruiter needs context to frame your background. If you use one, make it sharp and outcome-led: 'Backend engineer (8 YOE). Owned the auth + payments stack at a 40M-user fintech. Hiring focus: staff-level platform work at Series C+ infra.' Otherwise, go straight from contact info to experience.",
      },
      {
        q: "How important are GitHub and open-source contributions on a US tech CV?",
        a: "For mid-to-senior engineering roles in 2026, significant. Named open-source contributions or substantive side projects with public traction outperform yet another corporate bullet. A single line like 'Maintainer of OSS library X (3.1k GitHub stars, used by Stripe and Linear in production)' is one of the highest-leverage additions you can make. Empty or stale GitHubs are a quiet negative signal at FAANG-adjacent infra companies — better to omit the link than to display a desert.",
      },
      {
        q: "How do AI screening tools (Workday Talent Insights, Eightfold) change how I should write my resume?",
        a: "AI screeners do two things: extract structured fields (title, tenure, skills, scope) and compute keyword-overlap with the job description. To pass both: (1) use a linear, text-PDF format that parses cleanly — avoid Canva side-column templates; (2) read the JD twice and mirror exact tokens where they truthfully apply to your work — 'Kubernetes' beats 'container orchestration', 'PostgreSQL' beats 'relational database'; (3) keep your title and dates parseable (don't use ranges like 'Spring 2024' — use 'Mar 2024' or '03/2024'). The keyword game is real and the linear format is the unlock.",
      },
    ],
  },
  {
    slug: "curriculum-vitae-mexico-2026",
    title: "Currículum Vitae México 2026: Lo Que CDMX, Monterrey y Guadalajara Reclutadores Realmente Leen",
    description:
      "El mercado mexicano de tecnología en 2026 es el nearshore más grande de Norteamérica. Aquí está la estructura de currículum que está consiguiendo entrevistas en Kavak, Bitso, Clip, Konfío, Mercado Libre México y los hubs tech de CDMX, Monterrey y Guadalajara.",
    publishedAt: "2026-01-22",
    updatedAt: "2026-01-22",
    readingMinutes: 7,
    tags: ["Mexico", "CDMX", "LATAM", "Currículum"],
    heroAccent: "from-[#166534] to-[#15803D]",
    hero: {
      stat: "Nearshoring",
      statLabel: "MUEVE 60%+ DE LAS NUEVAS CONTRATACIONES TECH",
    },
    sections: [
      {
        kind: "lede",
        body:
          "El mercado laboral mexicano en 2026 está siendo redefinido por el nearshoring desde Estados Unidos y Canadá. Las contrataciones tech crecieron más del 30% interanual en CDMX, Monterrey y Guadalajara, con empresas como Kavak, Bitso, Clip, Konfío, Stori, Cuenca, Mercado Libre México, Rappi México, y las oficinas regionales de AWS, Microsoft, Google, Oracle, IBM y SAP compitiendo por el mismo talento. El CV que gana hoy combina formato global (en inglés cuando aplica) con señalización local precisa (RFC, CURP solo cuando se solicita, dominio del español + inglés ejecutivo).",
      },
      {
        kind: "h2",
        text: "La estructura del CV mexicano para tech (2026)",
      },
      {
        kind: "list",
        items: [
          {
            t: "Header — limpio, sin foto, idioma según destinatario.",
            d: "Nombre · Título · CDMX / Monterrey / Guadalajara / Querétaro · +52 teléfono · Email · LinkedIn · GitHub (ingeniería). Sin foto para roles tech / MNC / nearshore. Para MNCs con headquarters en EE.UU., presenta el CV en inglés; para empresas mexicanas, español ejecutivo limpio.",
          },
          {
            t: "Datos personales — mínimos.",
            d: "RFC, CURP, edad, estado civil y religión NO van en el CV moderno. La Ley Federal del Trabajo y los protocolos anti-discriminación de las MNCs (especialmente las que aplican EEOC compliance por ser subsidiarias gringas) excluyen explícitamente estos campos. Estado de trabajo: 'Mexicano', 'Residente permanente', 'Visa de trabajo USMCA disponible para EE.UU./Canadá'.",
          },
          {
            t: "Perfil profesional (3-4 líneas).",
            d: "Lidera con rol + sector mexicano + escala. 'Ingeniero backend con 7 años en fintech mexicana (Bitso, Konfío) — diseñé el motor de pagos que procesa MXN 18B anuales para 4M usuarios.' Para roles nearshore, ancla en USD + MXN: 'USD 4.5M ARR / MXN 78M anuales'.",
          },
          {
            t: "Experiencia laboral — bullets cuantificados.",
            d: "Orden cronológico inverso, rol / empresa / ciudad / fechas / 4-6 bullets cada uno. Cuantifica en pesos para roles locales y dobla la ancla (USD/MXN) para roles nearshore. Verbo de acción + alcance + impacto cuantificado: 'Lideré la migración a AWS multi-región — uptime de 99.5% a 99.99% y MXN 14M anuales en ahorro de infraestructura.'",
          },
          {
            t: "Educación + universidad.",
            d: "ITAM, ITESM (Tec de Monterrey), UNAM, IPN, La Salle, Anáhuac, Ibero — todas son Boolean-searches explícitas de reclutadores tier-1. Para roles globales, las universidades internacionales (Ivy, MIT, Stanford, Oxbridge, Bocconi, INSEAD para MBA) cargan peso significativo. Incluye el promedio (GPA en escala 10) si es 8.5+ y graduaste en los últimos 5 años.",
          },
          {
            t: "Idiomas — claves para nearshore.",
            d: "Inglés (Nativo / C1-Fluido / B2-Avanzado / B1-Intermedio / Básico) basado en CEFR o TOEFL/IELTS si tienes. Para roles nearshore, el inglés C1+ es prácticamente filtro obligatorio. Otros idiomas (portugués para roles LATAM expansivos, francés para roles en Quebec o Francia) suman puntos.",
          },
          {
            t: "Certificaciones — peso pesado.",
            d: "AWS (Solutions Architect, DevOps), Azure, GCP, PMP, Scrum Master, CISSP, CFA. Para finanzas: certificación CNBV, AMIB (mercados financieros). Para retail/logística: CSCMP, APICS.",
          },
        ],
      },
      {
        kind: "callout",
        title: "El nearshore — el mayor catalizador laboral del 2026",
        body:
          "Empresas estadounidenses están abriendo oficinas mexicanas a un ritmo récord para acceder a talento técnico en zonas horarias compatibles con EE.UU. y Canadá. Si has trabajado con clientes/equipos estadounidenses, hazlo explícito: 'Colaboración diaria con equipo en EST/PST', 'Stand-ups en inglés con stakeholders en Seattle y Austin', 'Dueño técnico de cuenta Fortune 500'. Esta señal vale un múltiplo 2x sobre 'experiencia internacional' genérica.",
      },
      {
        kind: "h2",
        text: "Longitud, formato y la pregunta del idioma",
      },
      {
        kind: "p",
        text:
          "Longitud: 2-3 páginas. Formato: PDF, nunca Word. Foto: solo para roles tradicionales en empresas familiares mexicanas (retail, hospitalidad, algunos sectores manufactureros). Carta de presentación: opcional en tech y nearshore; esperada en banca tradicional, gobierno, y algunos corporativos. Nombrar archivo: NombreCompleto_CV_2026.pdf. Versión en inglés: FirstName_LastName_Resume_2026.pdf — manda la versión apropiada al destinatario.",
      },
      {
        kind: "h2",
        text: "Keywords que reclutadores mexicanos buscan",
      },
      {
        kind: "list",
        items: [
          {
            t: "Sector + regulación local.",
            d: "'CNBV-regulado', 'Banxico-supervisado', 'AML/CFT México', 'fintech IFPE/ITF', 'SAT'. Servicios financieros y fintech filtran fuerte en estos.",
          },
          {
            t: "Nearshore + compatibilidad horaria.",
            d: "'Equipo distribuido US/MX', 'colaboración cross-border', 'overlap con EST/PST'. Estas frases son altísimas en búsquedas Boolean en 2026.",
          },
          {
            t: "Marcas mexicanas + escala.",
            d: "'Kavak-scale', 'Bitso-scale', 'Mercado Libre México'. Familiaridad con marcas locales de gran escala diferencia a candidatos locales de migrantes.",
          },
        ],
      },
      {
        kind: "cta",
        text: "Audita tu CV mexicano contra el benchmark de reclutadores CDMX",
        href: "/evaluate",
      },
    ],
    faqs: [
      {
        q: "¿Debo incluir foto en mi CV mexicano 2026?",
        a: "Para roles tech, MNC y nearshore — no. Las subsidiarias mexicanas de empresas estadounidenses aplican estándares anti-discriminación EEOC y descartan CVs con foto en el primer filtro. Empresas mexicanas modernas (Kavak, Bitso, Clip, Konfío, Mercado Libre México, Rappi México) y las oficinas regionales de AWS, Microsoft, Google, Oracle siguen la misma práctica. Las fotos siguen siendo aceptables solo para roles tradicionales en empresas familiares mexicanas (algunos sectores de retail, hospitalidad, manufactura tradicional), pero nunca son requeridas.",
      },
      {
        q: "¿Necesito incluir RFC, CURP o edad en mi CV?",
        a: "No. La Ley Federal del Trabajo y los protocolos anti-discriminación que aplican la mayoría de las MNCs y empresas tech modernas explícitamente excluyen estos campos del proceso de selección. RFC y CURP son datos que se solicitan en la fase de contratación formal, no en el CV inicial. Edad, estado civil, religión y número de hijos también deben omitirse. Lo único que sí va sobre tu situación legal: 'Mexicano', 'Residente permanente' o 'Elegible para visa USMCA' si aplicas a roles que requieren viajes a EE.UU. o Canadá.",
      },
      {
        q: "¿Qué tan importante es el inglés C1 para roles tech en México?",
        a: "Crítico para roles nearshore y prácticamente filtro obligatorio en empresas que sirven clientes estadounidenses o canadienses. Las oficinas mexicanas de AWS, Microsoft, Google, Oracle, IBM, SAP, Stripe, GitHub y las nuevas fintechs cross-border (Stori, Klar, Cuenca) hacen stand-ups en inglés y los reclutadores filtran agresivamente en nivel CEFR. Indica tu nivel con honestidad usando la escala C2/C1/B2/B1 o tus puntajes TOEFL (100+) / IELTS (7.0+). Sobre-reportar el nivel se detecta en la primera entrevista y cuesta credibilidad para futuras aplicaciones en la misma empresa.",
      },
      {
        q: "¿Debo enviar el CV en español o en inglés?",
        a: "Depende del destinatario. Para MNCs con headquarters en EE.UU./Canadá (oficinas regionales de FAANG, consultoras Big Four, fintechs cross-border) — envía en inglés. Para empresas 100% mexicanas (Kavak, Bitso, Konfío, Banorte, Bimbo, Femsa) — envía en español ejecutivo limpio. Cuando no estás seguro, envía la versión en inglés a empresas tech y la versión en español a empresas tradicionales mexicanas. Idealmente, mantén ambas versiones actualizadas y manda la versión apropiada según el contexto del reclutador.",
      },
      {
        q: "¿Cómo aprovecho la ola del nearshoring en mi CV?",
        a: "Hazla explícita y cuantificable. Si has trabajado con equipos o clientes estadounidenses/canadienses, agrega líneas concretas: 'Colaboración diaria con equipo en EST/PST', 'Stand-ups en inglés con stakeholders en Seattle, Austin y Toronto', 'Dueño técnico de cuenta Fortune 500 — entregables semanales en inglés'. Si has hecho deployments transfronterizos: 'Lideré integración con sistema de cliente en EE.UU. cumpliendo SOC 2 + México NMX-I-27001'. Esta señal vale un múltiplo 2x sobre frases genéricas como 'experiencia internacional' — los reclutadores nearshore buscan literalmente esta evidencia operacional.",
      },
    ],
  },
];

export function getPost(slug) {
  return POSTS.find((p) => p.slug === slug) || null;
}
